// oscillaLive.js -- Live Coding Console
// -----------------------------------------------
// Provides a DSL console for live-coding Oscilla scores.
// All execution goes through the existing handleCueTrigger()
// pipeline. No handler functions are imported or reimplemented.
//
// Everything else comes in by import, but for what other modules keep on
// window: animation.js's oscillaAnimRegistry (targets by uid) and
// runningAnimations (a Map), loaded first through cueDispatcher.js, and
// oscillaOSCClient.js's oscillaOSCMonitor. The room is room.js out and
// roomEvents.js in; the pure parts are the live*.js modules beside this one.

import { makeDraggable } from "../system/uiUtils.js";
import { isDedicatedView } from "../system/oscillaView.js";
import { sendSocketMessage } from "../system/room.js";
import { projectName } from "../system/projectContext.js";
import { handleCueTrigger, stopPeerSounds } from "../cues/cueDispatcher.js";
import { roomMode, takeRoomMode, hearsOthers, setHearsOthers, peerOpts } from "../system/roomMode.js";
import { isDrawControl } from "../cues/draw.js";
import { setKeyboardOwner, registerEscapeLayer } from "../system/keyLayers.js";
import { LIVE_KEYS, LIVE_SYNTAX } from "./liveKeys.js";
import * as ParamBus from "../control/paramBus.js";
import {
  scanExpressions, expressionAtLine, splitInlineTarget, checkExpression, planUids,
  settleRandom, settleTempo, tempoChangesIn, undoFor, flipToggle, commentedCode, commentedTakeBacks, lineSettings, resetsFor, expressionAtCaret, LINE_LEADERS, fadesFirst, synthsOf, animsOf, writeKeyPlan,
} from "./liveEval.js";
import { setHandlesShown, handlesShown } from "../system/mapHandles.js";
import { knownBlocks } from "../cues/useLive.js";
import { setSilent, activeAudioCues } from "../cues/audio/audioShared.js";
import { soundStates, activeSynthVoices } from "../cues/synth.js";
import { heldAnims } from "../cues/hold.js";
import { liveSignalTriggers } from "../control/signalTriggers.js";
import { liveBindings, stopBindingsOf } from "../control/paramBinding.js";
import { nextWholeBeat, msAtBeat } from "../cues/audio/beatGrid.js";
import { shareTimeline } from "../cues/audio/roomBeat.js";
import { clockCue, latencyCue, downbeatCue, watchRoomBeat, resumeClock } from "./liveClock.js";
import { expandCluster } from "../parser/cueUtils.js";
import { suggest, LIVE_PARAMS, CONSOLE_LINES } from "./liveComplete.js";
import { voices, textFiles, csvColumns, buses, speakersOf, onListsChanged } from "./liveLists.js";
import { groupNames } from "../cues/groups.js";
import { helpUrl } from "./liveDocs.js";
import { pickFolder, clearFolder, describeFolder, folderPaths } from "../system/mediaFolder.js";
import { styleNames } from "../cues/textStyles.js";
import { isFoldHead, foldSpan, expandText, moveFolds, visibleLine, lineEnd, redoneFold, reattach, foldNested, unfoldNested, foldedHeadLines, focusSpan, moveLineMarks } from "./liveFold.js";
import { initScenes, resetScenes, waitLater, cancelWaits } from "./liveScenes.js";
import { knownSnapshots, loadSnapshots, keepSnapshot, namesOf, findSnapshot, onSnapshotsChanged } from "./liveSnapshots.js";
import { linesLeftToScenes, sceneNameOf, sceneSpans, waitBeats } from "./liveScenePlan.js";
import { handleSceneCue } from "../system/sceneHook.js";
import { setConsoleRunner } from "../system/consoleHook.js";
import { cueParamSchema } from "../cues/cueSchema.js";
import { setMuted as setOscMuted } from "../system/oscillaOSCClient.js";
import { highlightCode, highlightLines, highlightTail, esc } from "./liveHighlight.js";
import { batched, chunkBySize } from "./liveOutbox.js";
import { newSeed } from "../system/seededRandom.js";
import { onReset, ownerSignal, releaseOwner } from "../system/teardown.js";

let panelEl = null;
let isOpen = false;
let isPicking = false;
let selectedElement = null;
let selectedTargetId = null;      // used in dedicated view (no DOM element)
let signalRefreshTimer = null;

// Cached DOM refs (set during buildPanel)
let editorEl = null;
let mirrorEl = null;              // highlighted copy of the editor text, behind it
let performMode = false;          // code over the whole score
let _centredAt = null;            // perform mode: caret + text last centred on
let outputEl = null;
let signalsEl = null;
let targetInputEl = null;
let targetInfoEl = null;
let pickBtnEl = null;
let signalFilterEl = null;
let tabBarEl = null;              // tab bar container
let oscMonitorEl = null;          // OSC monitor content area
let oscMonitorFilterEl = null;    // OSC monitor filter (shared with signals)
let chatEl = null;                // chat message container
let chatInputEl = null;           // chat text input
let monitorTab = "osc";           // "osc" or "signals"
let scoreCueIds = [];             // DSL cue expressions from the score SVG
let browseIdx = -1;               // current index in cue browser

// =================================================================
//  PERSISTENT BUFFER & NETWORK SHARING STATE
// =================================================================

const SAVE_DEBOUNCE_MS = 400;
const SEND_DEBOUNCE_MS = 600;
const BUFFER_STORAGE_PREFIX = "oscilla.livecodeBuffer";

let saveTimer = null;
let sendTimer = null;
let activeTab = "local";          // "local" or a remote clientName
const remoteBuffers = new Map();  // clientName -> { content, color, updatedAt }


// =================================================================
//  LOCAL PERSISTENCE (localStorage)
//  Follows controlXYShared.js pattern: prefix:projectName
// =================================================================

const getProjectName = () => projectName("unknown");

// The project the editor text belongs to. Set when a buffer is loaded, so a
// project switch saves the old text under the old name, not the new one.
let bufferProject = null;

// The name the buffer is kept under: its project's, unless the page names it
// (Oscilla Live's sessions, liveSessions.js: a buffer for each)
let bufferName = null;
export function setBufferName(name) { bufferName = name || null; }

/** Another named buffer in the editor, this one kept first (Oscilla Live: another session). */
export function switchBuffer(name) {
  saveBufferToStorage();
  resetScenes();                    // another buffer: its own scenes, none on yet
  lineSet.clear();                  // ... and its own lines
  _lineSetText = "";
  bufferName = name || null;
  if (editorEl) restoreLocalBuffer();
}

function bufferStorageKey(name = bufferName || bufferProject || getProjectName()) {
  return `${BUFFER_STORAGE_PREFIX}.${name}`;
}

/** A buffer kept under a name, { content, updatedAt, ... } -- or null. */
export function readBuffer(name) {
  try { return JSON.parse(localStorage.getItem(bufferStorageKey(name)) || "null"); }
  catch { return null; }
}

/** Keep text as a buffer under a name (a shared link's code, before it opens). */
export function storeBuffer(name, content, updatedAt = Date.now()) {
  try { localStorage.setItem(bufferStorageKey(name), JSON.stringify({ content, updatedAt })); }
  catch (e) { console.warn("[Live] localStorage save failed:", e); }
}

export function dropBuffer(name) {
  try { localStorage.removeItem(bufferStorageKey(name)); } catch { /* private mode */ }
}

/** The buffer as it is now, folded blocks in place. */
export const bufferText = () => (editorEl ? fullText() : "");

/** New text for a named buffer that is in the editor now (its file changed on disk: liveProject.js); kept, cursor where it was. */
export function takeBufferText(name, text) {
  if (!editorEl || bufferName !== name || activeTab !== "local") return false;
  const at = Math.min(editorEl.selectionStart, text.length);
  const top = editorEl.scrollTop;
  if (!folds.length) {
    // nothing folded: one edit, so Ctrl+Z takes the text back to what it was
    editRange(0, editorEl.value.length, text);
  } else {
    // the heads that were folded and still are heads, folded again
    const was = foldedFullLines();
    setEditorText(text);
    const lines = text.split("\n");
    refold(was.filter(l => isFoldHead(lines[l] ?? "")));
  }
  editorEl.setSelectionRange(at, at);
  editorEl.scrollTop = top;
  syncMirrorScroll();
  saveBufferToStorage();
  return true;
}

// Told each time a buffer is kept: (its name, its text) -- Live's project file (liveProject.js)
const bufferSavedHooks = new Set();
export function onBufferSaved(fn) { bufferSavedHooks.add(fn); return () => bufferSavedHooks.delete(fn); }

function saveBufferToStorage() {
  if (!editorEl || FOLLOW) return;
  // Showing someone else's buffer: the editor holds their text, not ours.
  if (activeTab !== "local") return;
  const content = fullText();
  for (const fn of bufferSavedHooks) fn(bufferName, content);
  // kept beside the project's file already (keepBrowserBuffer): still so while it says the same
  const was = loadBufferFromStorage();
  try {
    localStorage.setItem(bufferStorageKey(), JSON.stringify({
      ...(was?.keptAs && was.content === content ? { keptAs: was.keptAs } : {}),
      content,
      folded: foldedFullLines(),
      cursorPos: editorEl.selectionStart,
      scrollTop: editorEl.scrollTop,
      targetId: selectedTargetId || targetInputEl?.value.trim() || "",
      updatedAt: Date.now()
    }));
  } catch (e) {
    console.warn("[Live] localStorage save failed:", e);
  }
}

function loadBufferFromStorage() {
  try {
    const raw = localStorage.getItem(bufferStorageKey());
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function debouncedSaveBuffer() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(saveBufferToStorage, SAVE_DEBOUNCE_MS);
}

// =================================================================
//  NETWORK: SEND BUFFER UPDATES
//  Debounced to avoid flooding; sent through room.js (sendSocketMessage)
// =================================================================

function sendBufferUpdate() {
  if (!editorEl || FOLLOW) return;
  if (activeTab !== "local") return;
  if (!window.socket || window.socket.readyState !== WebSocket.OPEN) return;

  const content = fullText();
  // Cap at 10KB to keep buffer updates reasonable
  if (content.length > 10240) return;

  sendSocketMessage("livecode_buffer_update", {
    project: getProjectName(),
    content,
    clientName: window.localClientName || "unknown",
    clientColor: window.localClientColor || "#999"
  });
}

function debouncedSendBuffer() {
  clearTimeout(sendTimer);
  sendTimer = setTimeout(sendBufferUpdate, SEND_DEBOUNCE_MS);
}

function requestRemoteBuffers() {
  sendSocketMessage("livecode_buffer_request", {
    project: getProjectName()
  });
}


// =================================================================
//  TAB BAR UI
// =================================================================

function buildTabBar() {
  tabBarEl = document.createElement("div");
  tabBarEl.className = "livecode-tab-bar";
  renderTabs();
}

function renderTabs() {
  if (!tabBarEl) return;

  tabBarEl.innerHTML = "";

  // Local tab -- always first
  const localTab = document.createElement("button");
  localTab.className = "livecode-tab" + (activeTab === "local" ? " active" : "");
  localTab.textContent = "My Buffer";
  localTab.addEventListener("click", () => switchTab("local"));
  tabBarEl.appendChild(localTab);

  // Remote tabs
  for (const [name, buf] of remoteBuffers) {
    const tab = document.createElement("button");
    tab.className = "livecode-tab" + (activeTab === name ? " active" : "");
    tab.textContent = name;
    if (buf.color) {
      tab.style.borderBottomColor = buf.color;
    }
    tab.addEventListener("click", () => switchTab(name));
    tabBarEl.appendChild(tab);
  }
}

function switchTab(tabName) {
  if (!editorEl) return;

  // If leaving local tab, stash current content
  if (activeTab === "local" && tabName !== "local") {
    saveBufferToStorage();
  }

  activeTab = tabName;

  if (tabName === "local") {
    // Restore local buffer
    const saved = loadBufferFromStorage();
    setEditorText(saved?.content || "");
    refold(saved?.folded);
    editorEl.readOnly = false;
    editorEl.classList.remove("livecode-readonly");
    if (saved?.cursorPos != null) {
      editorEl.selectionStart = editorEl.selectionEnd = saved.cursorPos;
    }
  } else {
    // Show remote buffer (read-only)
    const buf = remoteBuffers.get(tabName);
    setEditorText(buf?.content || "");
    editorEl.readOnly = true;
    editorEl.classList.add("livecode-readonly");
  }

  renderTabs();
}


// =================================================================
//  NETWORK: INCOMING BUFFER MESSAGES
//  Called for the room's messages (roomEvents.js)
// =================================================================

/**
 * Handle incoming livecode buffer messages from the server.
 * Analogous to annotationsHandleSocketMessage().
 * @param {Object} data - parsed WebSocket message
 */
function livecodeHandleSocketMessage(data) {
  if (!data || !data.type) return;

  switch (data.type) {
    case "livecode_buffer_update": {
      const { clientName, content, clientColor, updatedAt } = data;
      if (!clientName) return;
      // Ignore own buffer echoes (server should not send, but guard)
      if (clientName === window.localClientName) return;

      remoteBuffers.set(clientName, {
        content: content || "",
        color: clientColor || "#999",
        updatedAt: updatedAt || Date.now()
      });

      // If this tab is currently viewed, update the textarea live
      if (activeTab === clientName && editorEl) {
        setEditorText(content || "");
      }

      renderTabs();
      break;
    }

    case "livecode_buffer_list": {
      const { buffers } = data;
      if (!buffers || typeof buffers !== "object") return;

      for (const [name, buf] of Object.entries(buffers)) {
        if (name === window.localClientName) continue;
        remoteBuffers.set(name, {
          content: buf.content || "",
          color: buf.clientColor || "#999",
          updatedAt: buf.updatedAt || 0
        });
      }

      renderTabs();
      break;
    }

    case "livecode_buffer_clear": {
      const { clientName } = data;
      if (!clientName) return;

      remoteBuffers.delete(clientName);

      // If we were viewing the disconnected client's tab, switch back
      if (activeTab === clientName) {
        switchTab("local");
      } else {
        renderTabs();
      }
      break;
    }
  }
}

// Register at module scope so it's ready before any WS messages arrive.
// roomEvents.js dispatches "oscilla:livecode-buffer" events to avoid a
// circular import (same pattern as oscilla:osc-in).
window.addEventListener("oscilla:livecode-buffer", (e) => {
  livecodeHandleSocketMessage(e.detail);
});


// =================================================================
//  CHAT
//  Lightweight text chat between connected performers.
//  Messages broadcast via server, no persistence.
// =================================================================

function sendChatMessage(text) {
  if (!text) return;

  // Show locally immediately
  appendChatLine(window.localClientName || "me", text, window.localClientColor || "#999");

  // Broadcast to others
  sendSocketMessage("livecode_chat", {
    project: getProjectName(),
    text,
    clientName: window.localClientName || "unknown",
    clientColor: window.localClientColor || "#999"
  });
}

function appendChatLine(name, text, color) {
  if (!chatEl) return;

  const line = document.createElement("div");
  line.className = "livecode-chat-line";

  const nameSpan = document.createElement("span");
  nameSpan.className = "livecode-chat-name";
  nameSpan.textContent = name;
  if (color) nameSpan.style.color = color;

  const textSpan = document.createElement("span");
  textSpan.className = "livecode-chat-text";
  textSpan.textContent = " " + text;

  line.appendChild(nameSpan);
  line.appendChild(textSpan);
  chatEl.appendChild(line);

  // Cap at 200 lines
  while (chatEl.children.length > 200) {
    chatEl.removeChild(chatEl.firstChild);
  }

  // Auto-scroll to bottom
  chatEl.scrollTop = chatEl.scrollHeight;
}

// Listen for incoming chat messages (roomEvents.js)
window.addEventListener("oscilla:livecode-chat", (e) => {
  const { clientName, text, clientColor } = e.detail || {};
  if (clientName && text) {
    appendChatLine(clientName, text, clientColor);
  }
});


// =================================================================
//  PUBLIC: initLiveConsole()
//  Called once from app.js at startup. Adds the topbar button.
// =================================================================

/**
 * @param {{ restore?: boolean }} [opts]  restore: false -- the page opens the
 *   console itself, later (Oscilla Live's landing, live.js), not as a reload left it
 */
export function initLiveConsole({ restore = true } = {}) {
  // scene(drop, fade:8): the named #$ blocks of the buffer, folded ones too (liveScenes.js).
  // A score's scene() can come before the console was ever opened: then the
  // buffer as it was kept, its lines run straight, what is said to the browser's console
  const sceneText = () => (editorEl ? fullText() : (readBuffer()?.content ?? ""));
  initScenes({
    lines: () => sceneText().split("\n"),
    blockExprs: head => {
      const lines = sceneText().split("\n"), span = foldSpan(lines, head);
      if (!span) return [];
      // fades first within each stretch between wait() lines, not across them (liveScenes.js runInTurn)
      const out = [];
      let stretch = [];
      const flush = () => { out.push(...fadesFirst(stretch)); stretch = []; };
      for (const e of scanExpressions(lines.slice(span[0], span[1] + 1).join("\n"))) {
        if (/^\s*wait\s*\(/i.test(e.expr)) { flush(); out.push(e); } else stretch.push(e);
      }
      flush();
      return out.map(e => e.expr).filter(e => !CONSOLE_ONLY.test(e));
    },
    run: expr => (editorEl ? runExpression({ expr }) : (handleCueTrigger(expr, false, true, null, { live: true }), true)),
    log: (text, kind) => (editorEl ? logOutput(text, kind) : console.info(`[scene] ${text}`)),
    flash: head => { if (!editorEl) return; const at = visibleLine(folds, head); flashLines(at.line, at.line, "ok"); },
  });
  // console(s1, perform): a score opening the console on a scene
  setConsoleRunner(consoleCue);
  // the snapshots read (liveSnapshots.js): a head's suggestions shown now
  onSnapshotsChanged(() => { if (editorEl && document.activeElement === editorEl) renderMirror(); });
  // Wire up the existing HTML button
  const btn = document.getElementById("live-console-toggle");
  if (btn) {
    btn.addEventListener("click", toggle);
  }

  // Save buffer on page unload
  window.addEventListener("beforeunload", saveBufferToStorage);
  window.addEventListener("beforeunload", saveSession);
  window.addEventListener("oscilla:project-loaded", onProjectLoaded);
  // connected (again) to the room: what we write, and what the others do
  window.addEventListener("oscilla:room-open", () => {
    askRoomMode();
    if (isOpen) { sendBufferUpdate(); requestRemoteBuffers(); }
  });
  // another performer's tempo() or downbeat(), or Link's: said here (liveClock.js)
  if (!FOLLOW) watchRoomBeat(logOutput, () => isOpen);
  // clock(link) was on in this browser: the Link helper joined again
  if (!FOLLOW) resumeClock(logOutput);
  // a sound paused, held, let go: its lines shade (soundLineStates)
  window.addEventListener("oscilla:sound-state", () => { if (isOpen) renderMirror(); });

  // Ctrl+Shift+L -- show / hide the console, from anywhere. By the key's
  // place (KeyL), so it is the same key on any keyboard layout.
  document.addEventListener("keydown", (e) => {
    if (!e.ctrlKey || !e.shiftKey || e.altKey || e.code !== "KeyL") return;
    e.preventDefault();
    e.stopPropagation();
    toggle();
  }, true);

  // Oscilla Live (restore: false) has its stage from the start: no project to wait for
  stageFromStart = !restore;
  if (FOLLOW) { initFollower(!restore); return; }
  keepSession = restore;
  if (!restore) return;

  // Back as it was before a reload
  const session = loadSession();
  // ?live=1 (Oscilla Live's entry page links to it): straight into the console
  if (new URLSearchParams(location.search).get("live") === "1" && !session?.open) openPanel();
  if (session?.open) {
    openPanel();
    if (session.perform) setPerformMode(true);
  }
}

/**
 * A new project: put the old text away under its own name, open the new
 * one's. A project can ask for the console (preferences.json liveConsole:
 * "open" or "perform"), so a piece made to be live-coded opens ready.
 *
 * A project can make its file the code (preferences.json liveConsoleFile:
 * true): text/livecode.txt read when it opens and written back as you type,
 * as Oscilla Live does (liveProject.js), in the buffer Live keeps for it --
 * so the score and Live share it, and the code is on the disk, not only in
 * this browser.
 */
async function onProjectLoaded() {
  mirrorLog.length = 0;        // what was run went with the last stage: a projector opened now is not sent it
  applyCentredTyping();
  const project = getProjectName();
  askRoomMode();
  let fileBuffer = null;
  if (!FOLLOW) {
    const { useProjectFile, stopProjectFile } = await import("./liveProject.js");
    if (window.oscillaPrefs?.liveConsoleFile) {
      const { bufferNameOf } = await import("./liveSessions.js");
      fileBuffer = bufferNameOf(await useProjectFile(project));
    } else stopProjectFile();
    if (project !== getProjectName()) return;          // another project opened meanwhile
  }
  // what this browser had for the project before its file was the code, as last typed
  const before = fileBuffer && bufferName !== fileBuffer ? readBuffer(project) : null;
  if (!editorEl) setBufferName(fileBuffer);
  else if (bufferProject !== project || bufferName !== fileBuffer) {
    if (activeTab !== "local") switchTab("local");
    clearTimeout(saveTimer);
    saveBufferToStorage();                            // the old text, under its own name
    setBufferName(fileBuffer);
    restoreLocalBuffer();
    remoteBuffers.clear();
    renderTabs();
    if (isOpen) { sendBufferUpdate(); requestRemoteBuffers(); }
  }
  if (before) await keepBrowserBuffer(project, fileBuffer, before);
  const want = FOLLOW ? null : window.oscillaPrefs?.liveConsole;
  if (want === "open" || want === "perform") {
    if (!isOpen) openPanel();
    if (want === "perform" && !performMode) setPerformMode(true);
  }
}

/**
 * The code this browser kept for a project before its file was the code
 * (liveConsoleFile): written beside the file, once, as
 * text/livecode-browser-<when>.txt -- the console going over to the file
 * must not put out of sight what was typed here.
 */
async function keepBrowserBuffer(project, fileName, old = readBuffer(project)) {
  if (!old?.content?.trim() || old.keptAs) return;
  if (old.content.trim() === (readBuffer(fileName)?.content ?? "").trim()) return;
  const d = new Date(old.updatedAt || Date.now());
  const p = n => String(n).padStart(2, "0");
  // to the second: two kept the same minute (this one and liveProject.js keepAside) do not overwrite each other
  const path = `text/livecode-browser-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.txt`;
  const form = new FormData();
  form.append("path", path);
  form.append("file", new Blob([old.content], { type: "text/plain" }), "livecode.txt");
  try {
    const res = await fetch(`/api/project/${encodeURIComponent(project)}/file`, { method: "POST", body: form });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    console.warn(`[Live] the code this browser had for ${project} could not be kept as ${path}:`, e);
    return;
  }
  // marked, so it is kept once (the buffer is no longer written: the file is the code now)
  try { localStorage.setItem(bufferStorageKey(project), JSON.stringify({ ...(readBuffer(project) || old), keptAs: path })); } catch { /* kept on disk all the same */ }
  const say = `the code this browser had for ${project} before its file was the code: kept as ${path}`;
  console.info(`[Live] ${say}`);
  logOutput(say, "info");
}

function restoreLocalBuffer() {
  bufferProject = getProjectName();
  loadStash(bufferProject);
  if (FOLLOW) return;                        // its text comes from the laptop
  const saved = loadBufferFromStorage();
  setEditorText(saved?.content || "");
  refold(saved?.folded);
  if (saved?.cursorPos != null) {
    editorEl.selectionStart = editorEl.selectionEnd = Math.min(saved.cursorPos, editorEl.value.length);
  }
  if (saved?.targetId && targetInputEl) targetInputEl.value = saved.targetId;
  _restoreScrollTop = saved?.scrollTop ?? null;
  if (isOpen) applyRestoredScroll();
  startingText = saved?.content ? null : loadStartingBuffer(bufferProject);
}

// An empty buffer's starting text, on its way (loadStartingBuffer): console(s1) waits for it
let startingText = null;

/**
 * A project's own starting text, text/livecode.txt, for an empty buffer:
 * a demo opens with its snippets, and clearing the buffer and reloading
 * brings them back. Never over anything typed meanwhile.
 */
async function loadStartingBuffer(project) {
  if (!project || project === "unknown") return;
  try {
    const res = await fetch(`/scores/${encodeURIComponent(project)}/text/livecode.txt`);
    if (!res.ok) return;
    const text = await res.text();
    if (bufferProject !== project || editorEl.value) return;
    setEditorText(text);
    editorEl.setSelectionRange(0, 0);
  } catch { /* no starting text: an empty buffer */ }
}

/**
 * console(...) from a score: the console opened -- on a scene's #$ block,
 * the caret on its head, and that scene gone to (as scene() goes, fade: via:
 * curve: quant: cut passed on) -- or put away.
 *
 *     console(s1)                   opened on #$ s1, that block alone in sight, and scene(s1)
 *     console(s1, perform)          ... the code over the whole stage (panel: back to a panel)
 *     console(s1, fade:8, via:black)    the change of scene as scene() makes it
 *     console(s1, run:0)            opened on the block, nothing run: the performer starts it
 *     console(s1, focus:all)        all the code in sight (focus:line, the line; focus:block, the default with a name)
 *     console(s1, snapshot:calm)    the block as kept under "calm" first (liveSnapshots.js)
 *     console(perform)              opened, no block; console(open) the same as a panel
 *     console(close)                put away (perform mode kept for next time); console(off)
 *     console(clear, close)         the piece from the top: every sound stopped, what the console
 *                                   drew taken away (held ones too), no scene on -- and put away
 *
 * A projector window (?follow=1) shows what the laptop's console shows, so
 * here it only goes to the scene, as a scene() cue would.
 */
async function consoleCue(ast) {
  const args = ast?.args || [];
  const has = k => args.some(a => a.type === k);
  const arg = k => args.find(a => a.type === k)?.value;
  const name = arg("name") ?? arg("uid");
  const off = v => v != null && /^(0|off|false|no)$/i.test(String(v));
  const go = () => {
    if (name == null || off(arg("run"))) return true;
    const keep = new Set(["fade", "via", "curve", "quant", "cut", "add"]);
    return handleSceneCue({ type: "cueScene", args: [{ type: "name", value: name }, ...args.filter(a => keep.has(a.type))] });
  };
  // clear: the stage as at the start of the piece -- the score's first cue, so a run
  // from the top does not open on what the last one left (a pinned spot over the title)
  if (has("clear")) {
    const { clearStage } = await import("./liveExamples.js");
    await clearStage({ keepTempo: true });
    resetScenes();
  }
  if (FOLLOW) return go();
  if (has("close")) { if (isOpen) closePanel(); return true; }

  if (!isOpen) openPanel();
  if (has("perform") && !performMode) setPerformMode(true);
  if (has("panel") && performMode) setPerformMode(false);
  // on a section, the block that is sounding alone in sight, unless asked otherwise
  const focus = arg("focus") ?? (name != null ? "block" : null);
  if (focus != null) {
    const to = off(focus) || String(focus) === "all" ? false : String(focus) === "line" ? "line" : "block";
    if (to !== focusMode) toggleFocusMode(to);
  }
  if (name == null) return true;

  // a buffer opened empty for the first time: its starting text (text/livecode.txt) first
  if (startingText) await startingText;
  const scene = sceneSpans(fullText().split("\n")).find(s => s.name === String(name));
  if (!scene) {
    logOutput(`console(${name}): no block headed #$ ${name}`, "err");
    return false;
  }
  if (activeTab === "local") {
    const head = visibleLine(folds, scene.head).line;
    // snapshot:calm -- the section as it was kept, every time it starts
    const snap = arg("snapshot");
    if (snap != null && !(await bringSnapshot(head, String(name), String(snap)))) return false;
    const at = lineEnd(editorEl.value, head);
    editorEl.setSelectionRange(at, at);
    _centredAt = null;
    renderMirror();
    sendViewSoon();
  }
  return go();
}

// =================================================================
//  SNAPSHOTS OF BLOCKS (liveSnapshots.js) AND THE FILE'S VERSIONS
// =================================================================

/** The console's own lines: kept out of scenes and of running everything. */
const CONSOLE_ONLY = new RegExp(`^\\s*(${CONSOLE_LINES.join("|")})\\s*\\(`, "i");

/** The editor's line the caret is on. */
const caretLine = () => editorEl.value.slice(0, editorEl.selectionStart).split("\n").length - 1;

/** The head of the block the editor's line `line` is in, or null: the nearest #$ above whose block holds it. */
function headAbove(line) {
  const lines = editorEl.value.split("\n");
  for (let h = Math.min(line, lines.length - 1); h >= 0; h--) {
    if (!isFoldHead(lines[h])) continue;
    if (h === line) return h;
    const span = folds.some(f => f.line === h) ? null : foldSpan(lines, h);
    if (span && line <= span[1]) return h;
  }
  return null;
}

/**
 * The block under the head at editor line `h`: its lines as text (the folds
 * inside it put back), and where they are -- { text, fold } when it is
 * folded, { text, from, to } (editor lines) when not (from > to: none yet).
 */
function blockLines(h) {
  const fold = folds.find(f => f.line === h);
  if (fold) return { text: fold.body.slice(1), fold };
  const lines = editorEl.value.split("\n");
  const span = foldSpan(lines, h);
  if (!span) return { text: "", from: h + 1, to: h };
  const inner = new Map(folds.filter(f => f.line >= span[0] && f.line <= span[1]).map(f => [f.line, f.body]));
  return { text: lines.slice(span[0], span[1] + 1).map((l, i) => l + (inner.get(span[0] + i) ?? "")).join("\n"), from: span[0], to: span[1] };
}

/** snapshot(name) on editor line `line`: the block it is in kept; snapshot() lists the block's. */
function snapshotHere(line, name) {
  const h = headAbove(line);
  const block = h == null ? null : sceneNameOf(editorEl.value.split("\n")[h]);
  if (!block) { logOutput("snapshot(): only in a block with a name (#$ s1)", "err"); return false; }
  const project = getProjectName();
  if (!name) {
    loadSnapshots(project).then(list => {
      const names = namesOf(list, block);
      logOutput(names.length ? `${block}'s snapshots: ${names.join(", ")} -- on its head, after the name, Tab brings one back` : `${block} has no snapshots yet: snapshot(calm) keeps it`, "info");
    });
    return true;
  }
  const { text } = blockLines(h);
  keepSnapshot(project, block, name, text).then(ok => logOutput(ok
    ? `snapshot: ${block} ${name} kept (${text.split("\n").length} lines)`
    : `snapshot: ${block} ${name} could not be written`, ok ? "ok" : "err"));
  return true;
}

/** The head line's snapshots, as suggestions: #$ s1 | or #$ s1 ca| -- or null when the caret is not there. */
function snapshotsAtHead(text, pos) {
  const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
  const end = text.indexOf("\n", pos);
  if (text.slice(pos, end < 0 ? text.length : end).trim()) return null;      // only at the line's end
  const m = /^\s*#\$+\s*([A-Za-z_][\w-]*)\s+([\w-]*)$/.exec(text.slice(lineStart, pos));
  if (!m) return null;
  const [, block, word] = m;
  const names = namesOf(knownSnapshots(getProjectName()), block).filter(n => n.startsWith(word) && n !== word);
  if (!names.length && !namesOf(knownSnapshots(getProjectName()), block).includes(word)) return null;
  const items = (names.length ? names : [word]).map(n => ({ label: n, insert: n.slice(word.length), snapshot: { block, name: n } }));
  return { word, kind: "snapshot", items, choices: true };
}

/**
 * The snapshot's lines in place of the block under the head at editor line
 * `h`, and its name on the head (#$ s1 calm). The block as it was is kept
 * first as its "last". An open block changes in one edit (Ctrl+Z); a folded
 * one stays folded, its lines changed. Resolves to true when it came in.
 */
async function bringSnapshot(h, block, name) {
  const project = getProjectName();
  const list = await loadSnapshots(project);
  const snap = findSnapshot(list, block, name);
  if (!snap) { logOutput(`snapshot: ${block} has no "${name}"${namesOf(list, block).length ? ` (there are: ${namesOf(list, block).join(", ")})` : ""}`, "err"); return false; }
  const now = blockLines(h);
  if (now.text !== snap.body && name !== "last") await keepSnapshot(project, block, "last", now.text);
  // the head says which: #$ s1 calm
  const lines = editorEl.value.split("\n");
  const head = lines[h].replace(/^(\s*#\$+\s*[A-Za-z_][\w-]*)\b.*$/, `$1 ${name}`);
  if (now.fold) {
    now.fold.body = "\n" + snap.body;
    delete now.fold.shown;
    delete now.fold.inner;
    now.fold.head = head;
    if (head !== lines[h]) editRange(lineEnd(editorEl.value, h) - lines[h].length, lineEnd(editorEl.value, h), head);
    renderMirror();
    debouncedSaveBuffer();
  } else {
    // folds inside it back in first, so the block's lines are all in the editor
    let inner;
    while ((inner = folds.find(f => f.line > h && f.line <= blockLines(h).to))) {
      const r = unfoldNested(editorEl.value, folds, inner);
      editRange(r.at, r.at, r.insert);
      folds = r.folds;
    }
    const { from, to } = blockLines(h);
    const text = editorEl.value;
    const a = lineEnd(text, h), b = from > to ? a : lineEnd(text, to);
    // an ordinary edit (not editRange): the folds below move with the lines it adds or takes
    editorEl.setSelectionRange(a - lines[h].length, b);
    insertText(head + (snap.body ? "\n" + snap.body : ""));
  }
  logOutput(`snapshot: ${block} ${name} brought back${name !== "last" ? ` (snapshot(last) or "last" on the head: as it was)` : ""}`, "ok");
  sendViewSoon();
  return true;
}

/**
 * versions(): the versions the server keeps of this code's file (a project's
 * text/livecode.txt, liveProject.js), newest first; versions(3) puts the
 * third back in the editor -- the code as it is now kept first, so
 * versions() lists it in turn.
 */
async function versionsCue(n) {
  const { projectFileOf } = await import("./liveProject.js");
  const project = projectFileOf();
  if (!project) { logOutput("versions(): this code is not a project's file -- preferences.json \"liveConsoleFile\": true, or Oscilla Live on a project", "err"); return; }
  const api = `/api/text-versions/${encodeURIComponent(project)}`;
  const q = `path=${encodeURIComponent("text/livecode.txt")}`;
  let list;
  try { list = (await (await fetch(`${api}?${q}`)).json()).versions || []; }
  catch { logOutput("versions(): the server could not be asked", "err"); return; }
  if (n == null) {
    if (!list.length) { logOutput("versions(): none kept yet", "info"); return; }
    const when = ts => new Date(ts).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
    list.slice(0, 12).forEach((v, i) => logOutput(`${i + 1}  ${when(v.ts)}  ${(v.size / 1024).toFixed(1)} KB${v.label ? `  ${v.label}` : ""}`, "info"));
    logOutput("versions(3) puts the third back (the code now is kept first)", "info");
    return;
  }
  const v = list[n - 1];
  if (!v) { logOutput(`versions(${n}): there are ${list.length}`, "err"); return; }
  try {
    saveBufferToStorage();
    await fetch(`${api}/snapshot`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ path: "text/livecode.txt", now: 1 }) });
    const res = await fetch(`${api}/file?${q}&file=${encodeURIComponent(v.file)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    if (!takeBufferText(bufferName, text)) throw new Error("the editor holds another buffer");
    logOutput(`versions(${n}): the code as it was ${new Date(v.ts).toLocaleString()} -- Ctrl+Z, or versions(1), for what it was just now`, "ok");
  } catch (e) { logOutput(`versions(${n}): ${e.message}`, "err"); }
}

// A hidden textarea has no scroll to set: the saved one waits for the panel.
let _restoreScrollTop = null;
function applyRestoredScroll() {
  if (_restoreScrollTop == null || !editorEl) return;
  const top = _restoreScrollTop;
  _restoreScrollTop = null;
  requestAnimationFrame(() => { editorEl.scrollTop = top; syncMirrorScroll(); });
}


// =================================================================
//  SESSION
//  A reload in the middle of a set comes back as it was: the console
//  open or not, perform mode on or off, the panel where it was put.
//  Kept in this browser only (localStorage) -- it is how this screen
//  was set up, not part of the project.
// =================================================================

const SESSION_KEY = "oscilla.livecode.session";

// false on a page that opens the console itself (Oscilla Live): it neither
// takes the score's console session nor leaves its own for the score to open
let keepSession = true;

function saveSession() {
  if (!panelEl || FOLLOW || !keepSession) return;
  const st = panelEl.style;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({
      open: isOpen,
      perform: performMode,
      geom: { left: st.left, top: st.top, width: st.width, height: st.height },
      offset: codeOffset(),
    }));
  } catch { /* storage unavailable: nothing to remember */ }
}

function loadSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); }
  catch { return null; }
}


// =================================================================
//  TOGGLE
// =================================================================

function toggle() {
  if (isOpen) {
    closePanel();
  } else {
    openPanel();
  }
}

function openPanel() {
  const fresh = !panelEl;
  if (!panelEl) buildPanel();
  if (fresh) {
    // where the panel was put before a reload
    const session = loadSession();
    const g = session?.geom || {};
    for (const k of ["left", "top", "width", "height"]) if (g[k]) panelEl.style[k] = g[k];
    if (Array.isArray(session?.offset)) setCodeOffset(...session.offset);
  }
  panelEl.style.display = "flex";
  isOpen = true;
  // the engine's warnings into the output: from the first time the console
  // opens, not in every score window whether it ever does or not
  installWarningMirror();
  if (performMode) {
    document.addEventListener("pointerup", refocusEditor, true);
    setKeyboardOwner(keysToEditor);
    performPointer(true);
  }
  applyRestoredScroll();
  saveSession();

  const btn = document.getElementById("live-console-toggle");
  if (btn) btn.classList.add("livecode-active");

  startSignalMonitor();
  loadPanelPreferences();
  applyFontSize();
  editorEl?.focus();

  // Send current local buffer to server so other clients can see it,
  // then request their buffers. Needed because restoring from localStorage
  // doesn't fire the input event, so the server may not have our buffer yet.
  sendBufferUpdate();
  requestRemoteBuffers();
}

function closePanel() {
  if (panelEl) panelEl.style.display = "none";
  isOpen = false;
  saveSession();

  // Immediate save on close
  if (activeTab === "local") saveBufferToStorage();

  const btn = document.getElementById("live-console-toggle");
  if (btn) btn.classList.remove("livecode-active");

  stopPicking();
  stopSignalMonitor();
  // Hidden, perform mode is kept for when it comes back -- but a click on
  // the score must not send focus (or keys) to a textarea nobody can see.
  document.removeEventListener("pointerup", refocusEditor, true);
  setKeyboardOwner(null);
  performPointer(false);
}


// =================================================================
//  FONT SIZE
// =================================================================

// The panel and perform mode keep a size each: a size readable from the back
// of a hall is far too big for a panel beside the score.
const FONT_SIZE_KEY = "oscilla.livecode.fontSize";
const PERFORM_FONT_SIZE_KEY = "oscilla.livecode.performFontSize";
const FONT_MIN = 9;
const FONT_MAX = 24;
const PERFORM_FONT_MIN = 14;
const PERFORM_FONT_MAX = 96;
const PERFORM_FONT_DEFAULT = 24;

function fontSetting() {
  return performMode
    ? { key: PERFORM_FONT_SIZE_KEY, prop: "--lc-perform-font-size", min: PERFORM_FONT_MIN, max: PERFORM_FONT_MAX, def: PERFORM_FONT_DEFAULT }
    : { key: FONT_SIZE_KEY, prop: "--lc-panel-font-size", min: FONT_MIN, max: FONT_MAX, def: 12 };
}

function currentFontSize() {
  const f = fontSetting();
  return parseFloat(getComputedStyle(panelEl).getPropertyValue(f.prop)) || f.def;
}

/** The code's size in px, for the panel or perform mode, kept for next time. */
function setFontSize(px) {
  if (!panelEl) return;
  const f = fontSetting();
  const next = Math.round(Math.min(f.max, Math.max(f.min, px)) * 10) / 10;
  panelEl.style.setProperty(f.prop, next + "px");
  try { localStorage.setItem(f.key, next); } catch {}
  _centredAt = null;
  syncMirrorScroll();
}

function adjustFontSize(delta) {
  if (!panelEl) return;
  setFontSize(Math.round(currentFontSize()) + (performMode ? delta * 2 : delta));
}

function applyFontSize() {
  if (!panelEl) return;
  for (const [key, prop] of [[FONT_SIZE_KEY, "--lc-panel-font-size"], [PERFORM_FONT_SIZE_KEY, "--lc-perform-font-size"]]) {
    let saved = null;
    try { saved = localStorage.getItem(key); } catch {}
    if (saved) panelEl.style.setProperty(prop, saved + "px");
  }
}


// =================================================================
//  CODE LAYER
//  The textarea's text is transparent; the <pre> behind it shows the
//  same text highlighted, with a backdrop per line and a flash on the
//  lines that just ran. Every change to the text goes through here.
// =================================================================

const FLASH_MS = 350;
const flashing = new Map();       // line index -> "ok" | "err"
let flashTimer = null;

function setEditorText(text) {
  folds = [];
  undoneFolds = [];
  deletedFolds = [];
  editorEl.value = text;
  _foldText = text;
  renderMirror();
}


// =================================================================
//  CHEATSHEET (Ctrl+Shift+H)
//  The console's keys and a reminder of the language, a faint column over
//  the score beside the console (not over the code: the two would collide);
//  it takes no clicks and typing goes on. Ctrl+Shift+H or Esc puts it away.
//  The tables are liveKeys.js.
// =================================================================

let cheatEl = null;

function cheatOpen() { return !!cheatEl && !cheatEl.hidden; }

function toggleCheatsheet(show = !cheatOpen()) {
  if (!panelEl) return;
  if (!cheatEl) {
    cheatEl = document.createElement("div");
    cheatEl.className = "livecode-cheat";
    cheatEl.setAttribute("aria-hidden", "true");
    const rows = (list, cls) => list.map(([a, b]) =>
      `<div class="lc-cheat-row"><span class="${cls}">${esc(a)}</span><span class="lc-cheat-what">${esc(b)}</span></div>`).join("");
    cheatEl.innerHTML =
      `<div class="lc-cheat-title">Oscilla Live</div>` +
      `<div class="lc-cheat-col"><div class="lc-cheat-head">keys</div>${rows(LIVE_KEYS, "lc-cheat-key")}</div>` +
      `<div class="lc-cheat-col"><div class="lc-cheat-head">the language</div>${rows(LIVE_SYNTAX, "lc-cheat-code")}</div>` +
      `<div class="lc-cheat-foot">Ctrl+Shift+H or Esc to put away</div>`;
    document.body.appendChild(cheatEl);
    registerEscapeLayer({ id: "live-cheatsheet", tier: "popup", isActive: cheatOpen, dismiss: () => toggleCheatsheet(false) });
  }
  cheatEl.classList.toggle("livecode-cheat-perform", !!performMode);
  if (show && !performMode) placeCheatsheet();
  else if (show) Object.assign(cheatEl.style, { left: "", right: "", width: "" });   // perform: the right edge (CSS)
  cheatEl.hidden = !show;
}

/** Beside the console, on the side with more room, never under its edge. */
function placeCheatsheet() {
  const r = panelEl.getBoundingClientRect();
  const leftRoom = r.left - 32, rightRoom = window.innerWidth - r.right - 32;
  const left = leftRoom >= rightRoom;
  const room = Math.max(leftRoom, rightRoom);
  Object.assign(cheatEl.style, {
    left: left ? "16px" : `${Math.round(r.right + 16)}px`,
    right: "auto",
    width: `${Math.round(Math.min(620, Math.max(room, 300)))}px`,
  });
}

// =================================================================
//  FOLDING
//  A line starting with #$ heads a block (to the next #$ line). Tab on
//  the head folds the block away and brings it back; the head line stays,
//  with what is written on it. Folded lines leave the textarea and are kept
//  in `folds` (liveFold.js): whatever needs all the code -- saving, the
//  shared buffer, running everything -- reads fullText().
// =================================================================

let folds = [];                   // [{ line, head, body }]
let _foldText = "";               // the editor text the folds were last placed against
let _folding = false;             // our own fold / unfold edit is under way
let undoneFolds = [];             // folds an undo brought back: a redo folds them again
let deletedFolds = [];            // folds deleted with their head: an undo brings them back

/** The buffer with every folded block in place. */
function fullText() {
  return editorEl ? expandText(editorEl.value, folds) : "";
}

/** The folded heads as lines of the full text, those inside folds too (for saving). */
function foldedFullLines() {
  return foldedHeadLines(folds);
}

/**
 * Fold again the heads saved as folded (full-text lines), bottom up: a
 * fold inside another is folded first, and goes in with it (foldNested).
 */
function refold(lines) {
  if (!Array.isArray(lines) || !lines.length) return;
  let text = editorEl.value;
  let made = [];
  for (const line of [...new Set(lines)].sort((a, b) => b - a)) {
    const r = foldNested(text, made, line);
    if (!r) continue;
    made = r.folds;
    text = r.text;
  }
  folds = made;
  editorEl.value = text;
  _foldText = text;
  renderMirror();
}

/** line -> lines hidden, for the mirror. */
function foldMarks() {
  return new Map(folds.map(f => [f.line, f.body.split("\n").length - 1]));
}

/** An edit our own code makes: undoable, and not taken for the performer's. */
function editRange(from, to, text) {
  _folding = true;
  try {
    editorEl.setSelectionRange(from, to);
    insertText(text);
  } finally { _folding = false; }
}

/**
 * After any edit: carry the folds along. Deleting a folded head deletes its
 * block (an undo brings both back, still folded); typing a head's #$ away
 * brings the block back below it.
 */
function placeFolds(inputType = "") {
  if (!editorEl) return;
  if (_folding) return;
  if (inputType === "historyUndo" && deletedFolds.length) {
    const back = reattach(deletedFolds, folds, _foldText, editorEl.value);
    if (back.length) {
      folds = [...moveFolds(folds, _foldText, editorEl.value).folds, ...back];
      deletedFolds = deletedFolds.filter(d => !back.some(b => b.body === d.body));
      _foldText = editorEl.value;
      return;
    }
  }
  // a redo of a fold: the block is folded again, not lost
  const redone = undoneFolds.length ? redoneFold(undoneFolds, _foldText, editorEl.value) : null;
  if (redone) {
    folds = [...moveFolds(folds, _foldText, editorEl.value).folds, redone];
    undoneFolds = undoneFolds.filter(d => d.body !== redone.body);
    _foldText = editorEl.value;
    return;
  }
  if (!folds.length) { _foldText = editorEl.value; return; }
  const m = moveFolds(folds, _foldText, editorEl.value);
  folds = m.folds;
  if (m.unfolded.length) undoneFolds = [...undoneFolds, ...m.unfolded].slice(-20);
  if (m.deleted.length) deletedFolds = [...deletedFolds, ...m.deleted].slice(-20);
  // a head that went: its block back where the head was
  for (const r of m.restore) {
    const before = editorEl.value;
    const caret = editorEl.selectionStart;
    editorEl.setRangeText(r.text, r.at, r.at, "preserve");
    if (caret >= r.at) editorEl.setSelectionRange(caret, caret);
    folds = moveFolds(folds, before, editorEl.value).folds;
    logOutput("no longer a #$ line: its folded block is back below it", "info");
  }
  _foldText = editorEl.value;
}

/** Tab on a head: fold its block, or bring it back. false when not on a head. */
function toggleFoldAtCaret() {
  if (!editorEl || editorEl.readOnly) return false;
  const pos = editorEl.selectionStart;
  if (pos !== editorEl.selectionEnd) return false;
  const text = editorEl.value;
  const line = text.slice(0, pos).split("\n").length - 1;
  if (!isFoldHead(text.split("\n")[line] ?? "")) return false;
  const at = lineEnd(text, line);
  const open = folds.find(f => f.line === line);
  // the folds inside a block go in and come out with it, still folded;
  // the ones below move with it (liveFold foldNested / unfoldNested)
  if (open) {
    const r = unfoldNested(text, folds, open);
    editRange(r.at, r.at, r.insert);
    folds = r.folds;
  } else {
    const r = foldNested(text, folds, line);
    if (!r) return true;                          // a head with nothing under it
    editRange(r.from, r.to, "");
    folds = r.folds;
  }
  _foldText = editorEl.value;
  editorEl.setSelectionRange(at, at);
  renderMirror();
  debouncedSaveBuffer();
  return true;
}

/**
 * The block under a head as expressions to run: [{ expr, startLine,
 * endLine }] in the editor's lines -- or, folded, with no lines (they are
 * not in the editor), and the head to flash.
 */
function blockAtHead(line) {
  const f = folds.find(x => x.line === line);
  if (f) return { exprs: scanExpressions(f.body.slice(1)).map(e => ({ expr: e.expr })), folded: true };
  const lines = editorEl.value.split("\n");
  const span = foldSpan(lines, line);
  if (!span) return { exprs: [], folded: false };
  const code = lines.slice(span[0], span[1] + 1).join("\n");
  return {
    exprs: scanExpressions(code).map(e => ({ ...e, startLine: e.startLine + span[0], endLine: e.endLine + span[0] })),
    folded: false,
  };
}

let _statesFailed = false;            // said once, not on every key
function renderMirror() {
  if (!mirrorEl || !editorEl) return;
  // the caret is drawn here only when nothing is selected: a selection
  // shows itself
  const collapsed = editorEl.selectionStart === editorEl.selectionEnd;
  let caret = collapsed && !editorEl.readOnly
    ? { pos: editorEl.selectionStart, focused: FOLLOW || document.activeElement === editorEl }
    : null;
  // the hints and the line states read the engine (sounds, signals, lists):
  // should one of them fail, the code still shows, without them -- the text
  // the performer types is this layer, and must never stop following
  let states = new Map();
  try {
    if (caret) caret = { ...caret, ghost: ghostShown(ghostAtCaret()), hint: hintAtCaret() };
    states = focusStates(soundLineStates());
  } catch (err) {
    if (!_statesFailed) console.error("[live console] the code's hints and line states failed:", err);
    _statesFailed = true;
  }
  showLines(highlightLines(editorEl.value, flashing, caret, foldMarks(), states), highlightTail(editorEl.value));
  _lastCaret = caretKey();            // drawn: the selectionchange this edit fires has nothing new
  if (performMode && caret?.focused && centredTyping()) keepCaretHigh();
  syncMirrorScroll();
}

/**
 * The layer's lines put in, only those that changed since the last time:
 * a whole buffer's markup written and laid out on every key was 30 ms at
 * 300 lines. The layer is line i at child 2i, a newline between lines,
 * and the tail (a space) after the last.
 */
let _shown = { el: null, lines: [], tail: "" };
function showLines(lines, tail) {
  const was = _shown;
  _shown = { el: mirrorEl, lines, tail };
  const nodes = mirrorEl.childNodes;
  if (was.el !== mirrorEl || nodes.length !== 2 * was.lines.length - 1 + (was.tail ? 1 : 0)) {
    mirrorEl.innerHTML = lines.join("\n") + tail;
    return;
  }
  const a = was.lines, b = lines;
  let p = 0, s = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p++;
  while (s < a.length - p && s < b.length - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++;
  if (was.tail) mirrorEl.lastChild.remove();
  const gone = a.length - p - s, come = b.slice(p, b.length - s);
  const range = document.createRange();
  const markup = html => range.createContextualFragment(html);
  if (gone) {
    // the lines replaced, and when none take their place, one newline beside them
    let from = nodes[2 * p], to = nodes[2 * (p + gone - 1)];
    if (!come.length) { if (to.nextSibling) to = to.nextSibling; else from = from.previousSibling; }
    range.setStartBefore(from);
    range.setEndAfter(to);
    range.deleteContents();
    if (come.length) range.insertNode(markup(come.join("\n")));
  } else if (come.length) {
    // lines added: before the first one kept, or after the last
    if (p < a.length) mirrorEl.insertBefore(markup(come.join("\n") + "\n"), nodes[2 * p]);
    else mirrorEl.appendChild(markup("\n" + come.join("\n")));
  }
  if (tail) mirrorEl.appendChild(document.createTextNode(tail));
}

/**
 * Perform mode: the line being typed stays on the middle of the screen,
 * going down or up -- the code scrolls under it instead, so the typing stays
 * in the top half and the bottom is the visuals'. (Room above the first line
 * and below the last for it: livecode.css.) Only when the caret or the text
 * has moved: a re-draw for a flash or a tag leaves a wheel-scroll where it is.
 */
/** Preferences: perform mode keeps the typing line in the middle (off: it scrolls as any editor does). */
const centredTyping = () => window.currentProjectPrefs?.liveConsoleCentredTyping === true;

/** The panel's look for it: the room above and below the code that centring needs (livecode.css). */
function applyCentredTyping() {
  panelEl?.classList.toggle("livecode-centred", centredTyping());
}

function keepCaretHigh() {
  const at = editorEl.selectionStart + ":" + editorEl.value.length;
  if (at === _centredAt) return;
  const mark = mirrorEl.querySelector(".lc-caret");
  if (!mark) return;
  _centredAt = at;
  const top = mark.getBoundingClientRect().top - mirrorEl.getBoundingClientRect().top + mirrorEl.scrollTop;
  // the middle of the window, not of the editor: the editor can run on
  // below the window's bottom edge
  const middle = Math.min(editorEl.clientHeight, window.innerHeight - editorEl.getBoundingClientRect().top) * 0.5;
  editorEl.scrollTop = Math.max(0, top - middle);
}

// Ctrl+Shift+B, in turn: the block being edited alone, then the line (the
// expression) at the caret alone, then all the code again -- the rest out of
// sight (still there, in its place), so the visuals have the screen. It
// follows the caret. The projector shows what the laptop does.
const FOCUS_STEPS = [false, "block", "line"];
let focusMode = false;

/** What is in sight with focus on: the caret's block, or its expression (a line, or the lines one spans). */
function focusRange(text, lines, caretLine, mode) {
  if (mode === "line") {
    const e = expressionAtLine(text, caretLine);
    return e ? [e.startLine, e.endLine] : [caretLine, caretLine];
  }
  return focusSpan(lines, caretLine);
}

/**
 * The lines outside the caret's block (or line) marked unfocused (CSS hides
 * them), with focus on -- the editor's, or (the projector) a laptop's text.
 */
function focusStates(states, text = editorEl.value, caret = editorEl.selectionStart, mode = focusMode) {
  if (!mode) return states;
  const lines = text.split("\n");
  const caretLine = text.slice(0, caret).split("\n").length - 1;
  const [a, b] = focusRange(text, lines, caretLine, mode);
  for (let i = 0; i < lines.length; i++) {
    if (i >= a && i <= b) continue;
    const s = states.get(i);
    states.set(i, { state: [...(s?.state || []), "unfocused"], tag: s?.tag ?? null });
  }
  return states;
}

function toggleFocusMode(to = FOCUS_STEPS[(FOCUS_STEPS.indexOf(focusMode) + 1) % FOCUS_STEPS.length]) {
  focusMode = to === true ? "block" : to;
  renderMirror();
  sendViewSoon();
  logOutput(focusMode === "block" ? "focus: the block at the cursor alone (Ctrl+Shift+B: the line)"
    : focusMode === "line" ? "focus: the line at the cursor alone (Ctrl+Shift+B: all of it)"
    : "focus off: all the code", "info");
}

/**
 * The lines of a sound that is paused (gate(s, 0)) or held (hold(s)): line
 * -> { state, tag } -- drawn dimmed, and tagged on the line that names it,
 * so a sound left running silent is not forgotten. Nothing to look for
 * (the usual case): no scan at all.
 */
function soundLineStates(text = editorEl.value) {
  const out = new Map();
  const { gated, held, banging = [], stopped = [], waiting = [] } = soundStates();
  const live = liveSignalTriggers();
  const bound = new Set(liveBindings().map(b => b.source));
  if (!gated.length && !held.length && !banging.length && !stopped.length && !waiting.length && !live.length && !bound.size) return out;
  if (_scanned.text !== text) _scanned = { text, exprs: scanExpressions(text) };
  for (const e of _scanned.exprs) {
    // a line running on a signal (trig:every(4, beat.hit)), or following one
    // (screen(invert:follow(every(4, beat.hit), 0, 1))): active while it does
    if (trigWatchers(e.expr, live).watching.length || boundParts(e.expr, bound).length) {
      for (let i = e.startLine; i <= e.endLine; i++) out.set(i, { state: ["active"], tag: i === e.startLine ? "active" : null });
      continue;
    }
    // a bang(s, every) line whose metro is going: active, as its sound's are
    const metro = /^\s*bang\s*\(\s*(\[[^\]]*\]|[\w-]+)\s*,/.exec(e.expr);
    if (metro) {
      const names = metro[1].replace(/[[\]\s]/g, "").split(",");
      if (names.some(n => banging.includes(n) || (n === "all" && banging.length))) {
        for (let i = e.startLine; i <= e.endLine; i++) out.set(i, { state: ["active"], tag: i === e.startLine ? "active" : null });
      }
      continue;
    }
    const m = /^\s*(?:\w+\s*>\s*)?(?:synth|audio|audioPool|audioImpulse)\s*\([^]*?\buid\s*:\s*([\w-]+)/.exec(e.expr);
    if (!m) continue;
    const name = m[1];
    const states = [gated.includes(name) && "paused", stopped.includes(name) && "stopped", held.includes(name) && "held", banging.includes(name) && "active", waiting.includes(name) && "waiting"].filter(Boolean);
    if (!states.length) continue;
    for (let i = e.startLine; i <= e.endLine; i++) out.set(i, { state: states, tag: i === e.startLine ? states.join(" · ") : null });
  }
  return out;
}

// The line states are drawn on every key and caret move: the buffer's
// expressions, and each one's parse, kept by their text (a parse is pure)
let _scanned = { text: null, exprs: [] };
const _checked = new Map();
function checkedOnce(expr) {
  let c = _checked.get(expr);
  if (!c) {
    if (_checked.size > 2000) _checked.clear();
    c = checkExpression(expr);
    _checked.set(expr, c);
  }
  return c;
}

/**
 * The parts of a console line run on a signal (text(..., trig:every(4, beat.hit))) that
 * are watching it now, each with its watcher: { target, watching: [{ part,
 * watcher }] }. Matched as the dispatcher keys them: cue type and name.
 */
function trigWatchers(text, live = liveSignalTriggers()) {
  const none = { target: null, watching: [] };
  if (!live.length || !/\btrig\s*:\s*(every\s*\(|[\w-]+\.\w)/.test(text)) return none;
  const { target, expr } = splitInlineTarget(text.replace(/\s+/g, " ").trim());
  const checked = checkedOnce(expr);
  if (checked.error) return none;
  const watching = [];
  for (const part of checked.parts) {
    const trig = part.ast?.args?.find(a => a.type === "trig")?.value;
    if (typeof trig !== "string" || !trig.includes(".")) continue;
    const who = /\buid\s*:\s*([\w-]+)/.exec(part.expr)?.[1] ?? /\(\s*\[?\s*([\w-]+)/.exec(part.expr)?.[1] ?? "";
    const watcher = live.find(w => w.type === part.ast.type && w.who === who && (!target || !w.el || w.el === target));
    if (watcher) watching.push({ part, watcher });
  }
  return { target, watching };
}

/** The parts of a console line, as run, that follow a signal now (follow(...) bound). */
function boundParts(text, bound = new Set(liveBindings().map(b => b.source))) {
  if (!bound.size || !/\(/.test(text)) return [];
  const { expr } = splitInlineTarget(text.replace(/\s+/g, " ").trim());
  const checked = checkedOnce(expr);
  // a shape(...) scale(...) line binds as a whole
  return checked.error ? [] : [expr, ...checked.parts.map(p => p.expr)].filter(x => bound.has(x));
}

// The caret moved (keys, clicks, selection) without the text changing.
let _lastCaret = "";
const caretKey = () => `${editorEl.selectionStart}:${editorEl.selectionEnd}:${document.activeElement === editorEl}`;
function onSelectionChange() {
  if (!editorEl || !isOpen) return;
  const key = caretKey();
  if (key === _lastCaret) return;
  _lastCaret = key;
  renderMirror();
  sendViewSoon();
}

function syncMirrorScroll() {
  if (mirrorEl && editorEl) mirrorEl.scrollTop = editorEl.scrollTop;
  sendViewSoon();
}

function flashLines(start, end, kind) {
  if (projectorCode && !FOLLOW) queueFlash([start, end, kind]);
  for (let i = start; i <= end; i++) flashing.set(i, kind);
  renderMirror();
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => { flashing.clear(); renderMirror(); }, FLASH_MS);
}


// =================================================================
//  PERFORM MODE
//  The console becomes the score's top layer: code over the whole
//  screen, big enough to project. The mouse goes through to the score
//  (drag a live rect, press a button) and the keyboard stays in the code.
// =================================================================

/**
 * In perform mode the console fills the band between the top bar and the
 * transport, not the whole window: the bars stay in reach (fullscreen,
 * play, the menus) and no line of code sits under them. Kept to the bars
 * as they show, hide or change size.
 */
let bandWatch = null;
function fitPerformBand() {
  if (!panelEl) return;
  const shown = el => {
    if (!el) return null;
    const cs = getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden" || Number(cs.opacity) < 0.1) return null;
    const r = el.getBoundingClientRect();
    return r.height > 0 && r.bottom > 0 && r.top < innerHeight ? r : null;
  };
  const top = shown(document.getElementById("top-bar"));
  const bottom = shown(document.getElementById("controls"));
  let t = top ? Math.max(0, top.bottom) : 0;
  let b = bottom ? Math.max(0, innerHeight - bottom.top) : 0;
  if (innerHeight - t - b < innerHeight * 0.3) t = b = 0;    // bars in the way of everything: cover them
  panelEl.style.setProperty("--lc-band-top", `${Math.round(t)}px`);
  panelEl.style.setProperty("--lc-band-bottom", `${Math.round(b)}px`);
}
function watchPerformBand(on) {
  bandWatch?.disconnect();
  window.removeEventListener("resize", fitPerformBand);
  bandWatch = null;
  if (!on) return;
  fitPerformBand();
  window.addEventListener("resize", fitPerformBand);
  const bars = ["top-bar", "controls"].map(id => document.getElementById(id)).filter(Boolean);
  const ro = new ResizeObserver(fitPerformBand);
  const mo = new MutationObserver(fitPerformBand);
  // they slide in and out: measured again when the slide is done
  for (const el of bars) {
    ro.observe(el);
    mo.observe(el, { attributes: true, attributeFilter: ["class", "style", "hidden"] });
    el.addEventListener("transitionend", fitPerformBand);
  }
  bandWatch = { disconnect: () => { ro.disconnect(); mo.disconnect(); for (const el of bars) el.removeEventListener("transitionend", fitPerformBand); } };
}

function setPerformMode(on) {
  if (!panelEl) return;
  performMode = !!on;
  _centredAt = null;
  panelEl.classList.toggle("livecode-perform", performMode);
  applyCentredTyping();
  watchPerformBand(performMode);
  if (performMode) document.addEventListener("pointerup", refocusEditor, true);
  else document.removeEventListener("pointerup", refocusEditor, true);
  setKeyboardOwner(performMode ? keysToEditor : null);
  performPointer(performMode && isOpen);
  editorEl?.focus({ preventScroll: true });
  // the wrap width changed: the layer must wrap as the textarea now does
  requestAnimationFrame(renderMirror);
  saveSession();
}

// =================================================================
//  SUGGESTIONS
//  The best completion of what is being typed shows as ghost text after
//  the caret, Tab takes it; Ctrl+Space lists the others. What can be
//  suggested where is liveComplete.js; what the console knows of names
//  and parameters is gathered here.
// =================================================================

// the most used first: a list shows eight
const CHANNELS = ["x", "y", "sin", "cos", "tvel", "angle", "t", "norm", "vel", "rad",
  "sx", "sy", "uniform", "hNorm", "sNorm", "lNorm", "line", "end", "value"];

/** Every name the performer can reach now: written in the buffer, or alive. */
function knownUids() {
  const names = new Set();
  const all = fullText();                    // folded code's names too
  for (const m of all.matchAll(/\buid\s*:\s*([\w-]+)/g)) names.add(m[1]);
  for (const m of all.matchAll(/\b(?:viz|hush)\(\s*([A-Za-z_][\w-]*)/g)) names.add(m[1]);
  for (const k of activeSynthVoices.keys()) if (!k.includes("__")) names.add(k);
  for (const k of activeAudioCues.keys()) if (!k.includes("__")) names.add(k);
  for (const k of window.activeCueTexts?.keys() || []) names.add(k);
  for (const el of document.querySelectorAll(".oscilla-shape, .oscilla-shape-cluster")) names.add(el.id);
  for (const g of groupNames()) names.add(g);           // group(agents, ...): a name for several
  names.add("self");                                    // the line's own shape (me. too, not offered)
  return [...names].filter(Boolean).sort();
}

/**
 * What a name publishes on the bus now: its channels (follow(organ.| ...).
 * self. (or me.) on a line is the line's own shape -- shape(uid:x ...) or x > ... --
 * and a cluster's members (x_0, x_1 ... expandCluster) count as it. null when it publishes
 * nothing yet: the usual channels are offered instead.
 */
function busChannels(owner, before = "") {
  let who = owner;
  if (owner === "self" || owner === "me") {
    who = (/\bshape\s*\(\s*(?:uid\s*:\s*)?([A-Za-z_][\w-]*)/.exec(before)
      || /^\s*([A-Za-z_][\w-]*)\s*>/.exec(before))?.[1];
  }
  if (!who) return null;
  const out = new Set();
  for (const path of Object.keys(ParamBus.snapshot(who))) {
    const dot = path.indexOf(".");
    const uid = path.slice(0, dot);
    if (uid === who || /^_\d+$/.test(uid.slice(who.length))) out.add(path.slice(dot + 1));
  }
  return out.size ? [...out].sort() : null;
}

/** The names publishing on the bus now (not the incoming OSC). */
function busSources() {
  const out = new Set();
  for (const path of Object.keys(ParamBus.snapshot(""))) {
    if (path.startsWith("osc:")) continue;
    const dot = path.indexOf(".");
    if (dot > 0) out.add(path.slice(0, dot));
  }
  return [...out].sort();
}

/** The OSC addresses heard so far, as follow() writes them: /pedal/1. */
function oscHeard() {
  return Object.keys(ParamBus.snapshot("osc:")).map(p => "/" + p.slice(4)).sort();
}

const completionContext = () => ({
  params: cue => LIVE_PARAMS[cue] || cueParamSchema(cue)
    // scaleXY: scale's parameters, and an axis each
    || (cue === "scaleXY" ? [{ key: "sx" }, { key: "sy" }, ...(cueParamSchema("scale") || [])] : null) || [],
  uids: knownUids(),
  blocks: knownBlocks(),                     // use(orion: the files in blocks/
  channels: CHANNELS,
  channelsOf: busChannels,
  sources: busSources,
  oscAddresses: oscHeard,
  phrases: stashPhrases,
  voices,                                    // say(voice:| -- installed (liveLists.js)
  textFiles,                                 // text(cycle:"| -- the project's text/
  csvColumns,                                // text(cycle:"x.csv", col:|
  buses,                                     // bus:| -- the fx buses made
  speakersOf,                                // speaker:| -- how many the line's voice has
  styles: styleNames,                        // text(style:| -- the presets made with style()
});

/** The suggestions at the caret, or null where none belong. */
function suggestionsAtCaret() {
  if (!editorEl || editorEl.readOnly || FOLLOW) return null;
  const pos = editorEl.selectionStart;
  if (pos !== editorEl.selectionEnd) return null;
  const text = editorEl.value;
  if (/[\w]/.test(text[pos] || "")) return null;          // in the middle of a word
  // the whole expression: a synth( opened on a line above is still open here
  const { before, line } = expressionAtCaret(text, pos);
  if (isFoldHead(line)) return snapshotsAtHead(text, pos);   // #$ s1 |: its snapshots
  if (/^\s*(\/\/|#)/.test(line)) return null;              // a comment
  return suggest(before, completionContext(), line);
}

/** The ghost: the best suggestion's rest, only once something is typed. */
function ghostAtCaret() {
  if (document.activeElement !== editorEl) return "";
  const s = suggestionsAtCaret();
  // only what continues the word: a match from its middle cannot be a ghost.
  // A stashed phrase continues the line, so it may ghost after a ( or a space.
  // a whole set, nothing typed yet: reverb: -> {mix:…, …}, or the keys left
  // in reverb:{mix:0.5, -- Tab takes them all (no second } if one follows)
  if (s?.rest && !s.word) {
    const after = editorEl.value.slice(editorEl.selectionStart).trimStart();
    return s.rest.endsWith("}") && after.startsWith("}") ? s.rest.slice(0, -1) : s.rest;
  }
  const best = s?.items[0];
  return best && !best.replace && (s.word || best.phrase) ? best.insert : "";
}

// The ghost sits in the line it follows, so a long one would wrap and push
// the code below out from under the textarea. A stashed phrase can run to
// lines: the ghost shows its start, and Tab takes it whole.
const GHOST_SHOWN = 40;
function ghostShown(ghost) {
  const first = ghost.split("\n")[0];
  return first.length > GHOST_SHOWN || first !== ghost ? first.slice(0, GHOST_SHOWN) + " …" : first;
}

/** Tab: the ghost taken -- a word begun in another case (envper) put right as it goes. */
function takeGhost() {
  const ghost = ghostAtCaret();
  if (!ghost) return false;
  const best = suggestionsAtCaret()?.items[0];
  if (best?.recase && best.insert === ghost) recaseTyped(best.recase);
  insertText(ghost);
  if (best?.snapshot && best.insert === ghost) bringSnapshot(caretLine(), best.snapshot.block, best.snapshot.name);
  return true;
}

/** The letters just typed before the caret, as `word` has them (envper -> envPer). */
function recaseTyped(word) {
  const pos = editorEl.selectionStart;
  if (editorEl.value.slice(pos - word.length, pos) === word) return;
  editorEl.setSelectionRange(pos - word.length, pos);
  insertText(word);
}

/**
 * The reminder: what can go here, read rather than chosen -- a faint line at
 * the end of the caret's line. After a cue's ( its common parameters; after
 * wave: (and every parameter with values of its own) the values; on a whole
 * name that begins others (env) all of them. The first letter typed ends it
 * and the ghost carries on. (Ctrl+Space still lists, to pick from.)
 */
const HINT_ITEMS = 10;
function hintAtCaret() {
  if (document.activeElement !== editorEl) return "";
  const s = suggestionsAtCaret();
  if (!s) return "";
  if (s.signature != null) return s.signature;     // in sinOsc( ...: its arguments
  if (s.rest && !s.word) return "";                // the ghost shows the whole set already
  const named = s.items.filter(i => !i.phrase && !i.replace && !i.insert.endsWith("("));
  let labels;
  if (s.opening || (s.choices && !s.word) || (s.kind === "channel" && !s.word)) labels = named.map(i => i.label);
  else if (s.ambiguous) labels = named.map(i => i.label);
  else return "";
  if (labels.length < (s.ambiguous ? 2 : 1)) return "";
  return labels.slice(0, HINT_ITEMS).join(" · ") + (labels.length > HINT_ITEMS ? " · …" : "");
}

let _suggestList = null;               // { el, items, index }

/** auto: opened by typing (key: with values of its own), not by Ctrl+Space. */
function openSuggestList(auto = false) {
  closeSuggestList();
  const s = suggestionsAtCaret();
  if (!s?.items.length) return;
  // a list that opened by itself offers a value's choices (view: wave: ...):
  // once the value is done -- a comma, a closing bracket -- it goes
  // (and a bracket just opened: rotate( -- the common parameters; the
  // first letter typed closes it and the ghost carries on)
  if (auto && !s.choices && !s.ambiguous && !s.opening) return;
  const caretEl = mirrorEl?.querySelector(".lc-caret");
  const r = caretEl?.getBoundingClientRect() || editorEl.getBoundingClientRect();
  const el = document.createElement("div");
  el.className = "livecode-suggest" + (performMode ? " livecode-suggest-perform" : "");
  el.style.left = `${Math.round(r.left)}px`;
  el.style.top = `${Math.round(r.bottom + 4)}px`;
  document.body.appendChild(el);
  _suggestList = { el, items: s.items.slice(0, 8), index: 0, auto };
  renderSuggestList();
}

function renderSuggestList() {
  const l = _suggestList;
  if (!l) return;
  l.el.innerHTML = "";
  l.items.forEach((it, i) => {
    const row = document.createElement("div");
    row.className = "livecode-suggest-item" + (i === l.index ? " active" : "");
    row.textContent = it.label;
    row.addEventListener("pointerdown", (e) => { e.preventDefault(); takeSuggestion(i); });
    l.el.appendChild(row);
  });
}

function takeSuggestion(i) {
  const it = _suggestList?.items[i];
  closeSuggestList();
  if (!it) return;
  if (it.replace) {             // found in the middle of the word: replace the word
    const pos = editorEl.selectionStart;
    editorEl.setSelectionRange(pos - it.replace, pos);
  }
  if (it.recase) recaseTyped(it.recase);
  insertText(it.insert);
  if (it.snapshot) bringSnapshot(caretLine(), it.snapshot.block, it.snapshot.name);
}

function closeSuggestList() {
  _suggestList?.el.remove();
  _suggestList = null;
}

// a list asked for in the background (the text/ files, the voices) has come
// in: the reminder at the caret, and a list open, show it now
onListsChanged(() => {
  if (!editorEl || document.activeElement !== editorEl) return;
  renderMirror();
  if (_suggestList) openSuggestList(_suggestList.auto);
});

/** Keys while the list is open; true when one was taken here. */
function handleListKey(e) {
  const l = _suggestList;
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    l.index = (l.index + (e.key === "ArrowDown" ? 1 : l.items.length - 1)) % l.items.length;
    l.auto = false;                       // chosen in: Enter takes it now
    renderSuggestList();
    return true;
  }
  if (e.key === "Tab" || e.key === "Enter") {
    if (e.ctrlKey) return false;          // ctrl+enter still runs the line
    // a list that opened by itself does not take Enter: it is still a new line
    if (e.key === "Enter" && l.auto) { closeSuggestList(); return false; }
    e.preventDefault();
    takeSuggestion(l.index);
    return true;
  }
  if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeSuggestList(); return true; }
  return false;
}


// =================================================================
//  THE PROJECTOR WINDOW (follow mode)
//  The laptop window has the console, makes the sound and shows code over
//  the visuals. A second window of the same project with ?follow=1 -- on
//  the projector, opened by projector() -- runs everything the laptop's
//  console runs, silently (its sounds play inside it, so its scopes draw,
//  but nothing reaches the speakers), with no console and no bars. Its
//  randomness is the laptop's (settleRandom). Ctrl+Shift+P on the laptop
//  shows the code there too, mirrored: text, caret, scroll, flashes,
//  sized to the projector's width. A projector window that opens or
//  reloads mid-set asks, and the laptop replays what it has run.
//  Both windows share localStorage: the follower never writes it.
// =================================================================

const FOLLOW = new URLSearchParams(window.location.search).get("follow") === "1";
let projectorCode = false;               // laptop: is the code showing on the projector
const mirrorLog = [];                    // laptop: what it ran, for a projector that joins late
const MIRROR_LOG_MAX = 500;

// sent a task's worth at a time (liveOutbox.js): a relay drops past 60 a second
const sendRuns = (type, runs, key = "runs", extra = {}) => {
  for (const chunk of chunkBySize(runs)) sendSocketMessage(type, { project: getProjectName(), [key]: chunk, ...extra });
};
const queueRun = batched(runs => sendRuns("livecode_mirror", runs));
const queueFlash = batched(flashes => { if (projectorCode) sendView({ flashes }); });

function mirrorRun(payload) {
  if (FOLLOW) return;
  const msg = { ...payload, project: getProjectName() };
  mirrorLog.push(msg);
  if (mirrorLog.length > MIRROR_LOG_MAX) mirrorLog.shift();
  queueRun(msg);
}

/** Laptop: the stage was cleared for another piece (liveExamples.js): the projector's too, and nothing of the last to replay. */
export function mirrorClear() {
  if (FOLLOW) return;
  mirrorLog.length = 0;
  mirrorRun({ clear: true });
}

function viewState(extra = {}) {
  const [dx, dy] = panelEl ? codeOffset() : [0, 0];
  const room = editorEl ? editorEl.scrollHeight - editorEl.clientHeight : 0;
  return {
    project: getProjectName(),
    show: projectorCode,
    content: editorEl?.value ?? "",
    folds: [...foldMarks()],
    caret: editorEl?.selectionStart ?? 0,
    focus: focusMode,
    scroll: room > 0 ? editorEl.scrollTop / room : 0,
    fontSize: panelEl ? parseFloat(getComputedStyle(panelEl).getPropertyValue("--lc-perform-font-size")) || PERFORM_FONT_DEFAULT : PERFORM_FONT_DEFAULT,
    dx, dy,
    width: window.innerWidth,
    ...extra,
  };
}

function sendView(extra) {
  if (FOLLOW) return;
  sendSocketMessage("livecode_view", viewState(extra));
}

let _viewTimer = null;
function sendViewSoon() {
  if (FOLLOW || !projectorCode) return;
  clearTimeout(_viewTimer);
  _viewTimer = setTimeout(() => sendView(), 50);
}

function toggleProjectorCode() {
  projectorCode = !projectorCode;
  sendView();
  logOutput(`code on the projector: ${projectorCode ? "on" : "off"}`, "info");
}

function openProjector() {
  const url = new URL(window.location.href);
  url.searchParams.set("project", getProjectName());
  url.searchParams.set("follow", "1");
  url.searchParams.delete("view");
  const w = window.open(url.toString(), "oscilla-projector", "popup,width=1280,height=720");
  logOutput(w ? "projector window open: drag it to the projector, F11 for full screen"
              : "the browser blocked the window: allow pop-ups for this page", w ? "info" : "err");
}

// a scene's lines come mirrored one by one: scene(drop) run here would run
// this window's own block of that name as well; console() opens a console;
// folder() is this machine's own
const NOT_MIRRORED = /^\s*(scenes?|console|folder)\s*\(/i;

/**
 * Projector: run what the laptop ran, as it ran it. And in a jam, a laptop
 * runs what the others ran (peer): their sound noted, or passed by on a
 * machine that hears only its own (cueDispatcher.js soundsHere).
 */
async function runMirrored(m, peer = false) {
  if (m.project && m.project !== getProjectName()) return;
  try {
    if (m.clear) {
      // a jam's stage is one: what this laptop ran went with it, here and everywhere
      if (peer) mirrorLog.length = 0;
      await (await import("./liveExamples.js")).clearStage({ mirror: false });
      return;
    }
    if (m.unfollow) { for (const src of m.unfollow) stopBindingsOf(src); return; }
    if (m.mapShown != null) { setHandlesShown(m.mapShown); return; }
    // the laptop's seed, and the hits its signal lines have counted (a replay)
    const opts = { live: true, allVoices: !!m.allVoices, seed: m.seed, hits: m.hits, ...(peer ? peerOpts() : {}) };
    if (m.shapeLed) { handleCueTrigger(m.expr, false, true, null, { ...opts, source: m.source }); return; }
    const el = m.target ? findTargetElement(m.target) : null;
    for (const part of m.parts || []) {
      if (NOT_MIRRORED.test(part.expr)) continue;
      handleCueTrigger(part.expr, false, true, part.el ? el : null, { ...opts, source: part.source });
    }
  } catch (err) {
    console.warn("[live projector] could not run", m.expr, err);
  }
}

/**
 * Projector: each laptop's code as it is now, a column each, side by side --
 * every performer in a room who has Ctrl+Shift+P on; their own Ctrl+Shift+P
 * takes only their column away. `from` and `color` are the room's: the
 * sender's name and colour. Each column is that laptop's screen scaled into
 * it: its font, its offset, its scroll, its flashes; the name over it when
 * there are two or more.
 */
const columns = new Map();           // from -> { v, el, pre, flash: Map, timer }

function applyView(v) {
  const who = v.from ?? "";
  if (!v.show) { dropColumn(who); return; }
  if (!isOpen) openPanel();
  if (!performMode) setPerformMode(true);
  let c = columns.get(who);
  if (!c) {
    const el = document.createElement("div");
    el.className = "livecode-code lc-column";
    el.innerHTML = `<span class="lc-column-name"></span><pre class="livecode-mirror" aria-hidden="true"></pre>`;
    columnsEl().appendChild(el);
    c = { el, pre: el.querySelector("pre"), flash: new Map(), timer: null };
    columns.set(who, c);
  }
  c.v = v;
  const name = c.el.querySelector(".lc-column-name");
  name.textContent = who;
  name.style.color = v.color || "";
  for (const [a, b, kind] of v.flashes || []) {
    for (let i = a; i <= b; i++) c.flash.set(i, kind);
    clearTimeout(c.timer);
    c.timer = setTimeout(() => { c.flash.clear(); drawColumn(c); }, FLASH_MS);
  }
  layoutColumns();
}

/** The row the columns stand in, in the console's code area. */
function columnsEl() {
  let row = panelEl.querySelector(".lc-columns");
  if (!row) {
    row = document.createElement("div");
    row.className = "lc-columns";
    panelEl.querySelector('[data-section="editor"]').prepend(row);
  }
  return row;
}

function dropColumn(who) {
  const c = columns.get(who);
  if (!c) return;
  clearTimeout(c.timer);
  c.el.remove();
  columns.delete(who);
  if (!columns.size) { if (isOpen) closePanel(); return; }
  layoutColumns();
}

/** A column more or fewer: every column's width changed, so each is drawn again. */
function layoutColumns() {
  panelEl.classList.toggle("lc-many", columns.size > 1);
  for (const c of columns.values()) drawColumn(c);
}

function drawColumn(c) {
  const v = c.v;
  const text = v.content ?? "";
  // the laptop's layout, scaled to this column's width
  const k = (c.el.clientWidth || window.innerWidth) / (v.width || window.innerWidth);
  const size = `${(v.fontSize || PERFORM_FONT_DEFAULT) * k}px`;
  c.el.style.setProperty("--lc-perform-font-size", size);
  c.el.style.setProperty("--lc-font-size", size);
  c.el.style.setProperty("--lc-dx", `${Math.round((v.dx || 0) * k)}px`);
  c.el.style.setProperty("--lc-dy", `${Math.round((v.dy || 0) * k)}px`);
  const caret = { pos: Math.min(v.caret ?? 0, text.length), focused: true };
  const focus = v.focus === true ? "block" : (v.focus || false);     // true: an older laptop's block
  const states = focusStates(soundLineStates(text), text, caret.pos, focus);
  c.pre.innerHTML = highlightCode(text, c.flash, caret, new Map(v.folds || []), states);
  requestAnimationFrame(() => { c.pre.scrollTop = (v.scroll || 0) * (c.pre.scrollHeight - c.pre.clientHeight); });
}

/**
 * Laptop: the log, each line on a signal with the hits it has counted, so a
 * projector that joins late rolls the next hit's numbers as the laptop does.
 */
function withHits(log) {
  const counted = new Map(liveSignalTriggers().map(t => [t.source, t.hits]));
  if (!counted.size) return log;
  return log.map(m => {
    const lines = [m.source, m.expr, ...(m.parts || []).map(p => p.source)].filter(s => counted.has(s));
    return lines.length ? { ...m, hits: Object.fromEntries(lines.map(s => [s, counted.get(s)])) } : m;
  });
}

// in the order sent, each run done before the next: a cleared stage is
// cleared before the next piece's lines run on it
let _mirrored = Promise.resolve();
const runInTurn = (runs, peer = false) => {
  _mirrored = _mirrored.then(async () => { for (const m of runs || []) await runMirrored(m, peer); });
};
// a replay is a laptop's log from its last clear: run on a stage that has
// another laptop's replay on it already, that clear would take it away
const unclear = log => (log || []).filter(m => !m.clear);

function onFollowMessage(e) {
  const d = e.detail || {};
  if (d.project && d.project !== getProjectName()) return;
  if (d.type === "livecode_mode") { roomModeSaid(d); return; }
  if (!FOLLOW) {
    // a projector window has (re)joined, or a laptop joined a jam: what it
    // missed (to it alone), and the code view
    if (d.type === "livecode_view_request" && (isOpen || mirrorLog.length)) {
      sendRuns("livecode_mirror_replay", withHits(mirrorLog), "log", d.asker != null ? { to: d.asker } : {});
      sendView();
    }
    // a jam: the others' lines run here too. A ?view=live console's (exec)
    // came as livecode_exec already (socket.js); in a replay there is no
    // other way it comes
    if (roomMode() === "jam" && !isDedicatedView()) {
      if (d.type === "livecode_mirror") runInTurn(d.runs?.filter(m => !m.exec), true);
      else if (d.type === "livecode_mirror_replay") runInTurn(unclear(d.log), true);
    }
    return;
  }
  if (d.type === "livecode_mirror") runInTurn(d.runs);
  else if (d.type === "livecode_mirror_replay") runInTurn(unclear(d.log));
  else if (d.type === "livecode_view") applyView(d);
}
window.addEventListener("oscilla:livecode-follow", onFollowMessage);
// a line something on the stage made by hand -- a shape dragged (shape.js
// mirrorDrag): run by the projector window too, and replayed to a late one
// (key: the latest of each kept for the replay -- a long drag does not push
// the lines that made the shapes out of it)
window.addEventListener("oscilla:live-mirror", (e) => {
  const { expr, key } = e.detail || {};
  if (!expr) return;
  if (key) { const i = mirrorLog.findIndex(m => m.key === key); if (i >= 0) mirrorLog.splice(i, 1); }
  mirrorRun({ shapeLed: true, expr, key, target: null, parts: [] });
});

// A word for the console from the engine, whenever it comes: what a line
// set going says later than the few seconds a warning is mirrored for --
// adc()'s input refused once its prompt is answered (adc.js). Once only:
// the same text just mirrored is not said again.
window.addEventListener("oscilla:livecode-say", (e) => {
  const text = String(e.detail?.text ?? "");
  if (!text || !isOpen || text === _lastMirrored) return;
  _lastMirrored = text;
  logOutput(text, e.detail?.kind || "warn");
});

// =================================================================
//  THE ROOM'S MODE (roomMode.js): ensemble or jam
//  In ensemble each laptop sounds its own lines and the projector runs
//  everyone's; in a jam every laptop runs the others' lines too, from the
//  mirror the projector runs (onFollowMessage), with their seeds, on the
//  beat grid they share, and a laptop that joins late is replayed what
//  they ran. room(jam) / room(ensemble) sets it for the whole room, which
//  keeps it (server/liveRoom.js); room(hear:mine) / room(hear:all) is
//  this machine's own: the others' lines here without their sound, or
//  with it; room() says both.
// =================================================================

let stageFromStart = false;              // Oscilla Live: no project to wait for

/**
 * Ask the room for its mode -- on joining, and on each project (a mode is a
 * project's) -- once there is a stage to run the others' lines on: a jam's
 * replay run before a project has loaded would go with the loading.
 */
function askRoomMode() {
  if (FOLLOW) return;                    // a projector runs everyone's in either mode
  if (!stageFromStart && !window._projectLoaded) return;   // the project's load asks
  sendSocketMessage("livecode_mode_request", { project: getProjectName() });
}

const modeWords = mode => (mode === "jam" ? "jam: every machine runs every member's lines" : "ensemble: each machine sounds its own lines");

/** The room's mode, as it said it: the answer to askRoomMode, or someone's room(). */
function roomModeSaid(d) {
  const changed = takeRoomMode(d.mode);
  if (FOLLOW) return;
  if (changed && roomMode() === "jam") {
    // what the others have run so far: replayed to this laptop alone (a
    // ?view=live window has no stage to run it on)
    if (!isDedicatedView()) sendSocketMessage("livecode_view_request", { project: getProjectName() });
  } else if (changed) stopPeerSounds();  // each machine its own again: the others' sounds here go
  if (changed || d.from) logOutput(`room: ${modeWords(roomMode())}${d.from ? ` (${d.from})` : ""}`, "info");
}

/** room(jam), room(ensemble), room(hear:mine), room(hear:all), room(): true when done. */
function roomCue(args) {
  const words = args.split(",").map(w => w.trim()).filter(Boolean);
  for (const w of words) {
    const hear = /^hear\s*:\s*(mine|all)$/i.exec(w);
    if (/^(jam|ensemble)$/i.test(w)) {
      if (window.socket?.readyState !== WebSocket.OPEN) {
        logOutput("not in a room: the mode is a room's (join one, or open the score on a server)", "err");
        return false;
      }
      sendSocketMessage("livecode_mode", { project: getProjectName(), mode: w.toLowerCase() });
    } else if (hear) {
      const all = hear[1].toLowerCase() === "all";
      setHearsOthers(all);
      const stopped = all ? 0 : stopPeerSounds();
      logOutput(all ? "the others' lines sound here again, from their next run"
                    : `the others' lines run here without their sound${stopped ? ` (${stopped} of their sounds stopped)` : ""}`, "info");
    } else {
      logOutput(`room() takes jam, ensemble, hear:mine or hear:all -- not ${w}`, "err");
      return false;
    }
  }
  if (!words.length) {
    logOutput(`room: ${modeWords(roomMode())}; ${hearsOthers() ? "the others' sound on here" : "the others' sound off here (room(hear:all) to hear it)"}`, "info");
  }
  return true;
}

function initFollower(stageReady) {
  document.body.classList.add("oscilla-follow");
  setSilent(true);                       // the laptop makes the sound
  setOscMuted(true);                     // ... and the OSC
  // Ask the laptops for what they have run, once there is a stage and a socket
  const ask = () => {
    if (window.socket?.readyState === WebSocket.OPEN) {
      setOscMuted(true);                 // again: loading the project sets it from its preferences
      sendSocketMessage("livecode_view_request", { project: getProjectName() });
    } else setTimeout(ask, 300);
  };
  // a performer gone from the room: their column goes with them
  window.addEventListener("oscilla:livecode-buffer", (e) => {
    if (e.detail?.type === "livecode_buffer_clear") dropColumn(e.detail.clientName ?? "");
  });
  if (stageReady || window._projectLoaded) ask();
  else window.addEventListener("oscilla:project-loaded", ask, { once: true });
}


// =================================================================
//  POINTING AT THE CODE IN PERFORM MODE
//  The textarea lets the mouse through to the score. The code column --
//  from the code's left edge to the end of its longest line, its first
//  line to its last, blank lines included -- takes it instead: a click
//  puts the cursor there, a drag selects, a double click takes a word, a
//  wheel or two-finger swipe scrolls. Right of the code and below it, the
//  mouse is the score's. The layer behind the textarea holds its text
//  character for character, so a point in it is a place in the text.
// =================================================================

/** The code column on screen, or null when there is no code. */
function codeColumn() {
  const bands = mirrorEl?.querySelectorAll(".lc-line:not(.lc-blank)");
  if (!bands?.length) return null;
  const m = mirrorEl.getBoundingClientRect();
  const left = m.left + parseFloat(getComputedStyle(mirrorEl).paddingLeft || 0);
  let right = left, top = Infinity, bottom = -Infinity;
  for (const band of bands) {
    for (const r of band.getClientRects()) {
      if (r.bottom < m.top || r.top > m.bottom) continue;       // scrolled out of sight
      right = Math.max(right, r.right); top = Math.min(top, r.top); bottom = Math.max(bottom, r.bottom);
    }
  }
  if (top === Infinity) return null;
  return { left: m.left, right, top: Math.max(m.top, top), bottom: Math.min(m.bottom, bottom) };
}

const inCodeColumn = (x, y) => {
  const c = codeColumn();
  return !!c && x >= c.left && x <= c.right && y >= c.top && y <= c.bottom;
};

/**
 * The text offset under a screen point. While pointing, the whole layer
 * takes the pointer (.livecode-pointing), so a blank line or the space past
 * a line's end resolves to the nearest place in the text, not the score.
 */
function textOffsetAt(x, y) {
  let node = null, off = 0;
  if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y);
    node = p?.offsetNode; off = p?.offset ?? 0;
  } else if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    node = r?.startContainer; off = r?.startOffset ?? 0;
  }
  if (!node || !mirrorEl?.contains(node)) return null;
  const range = document.createRange();
  range.setStart(mirrorEl, 0);
  range.setEnd(node, off);
  return Math.min(range.toString().length, editorEl.value.length);
}

const WHEEL_LATCH_MS = 250;
let _anchor = null;
let _wheelUntil = 0;
let _lastDown = { t: 0, x: 0, y: 0 };      // a second press soon after, close by: a double click

const pointing = (on) => panelEl?.classList.toggle("livecode-pointing", on);
// the header's buttons, a text on the screen and a shape with drag:1 (dragged by their own hand)
const ownsPointer = (e) =>
  performMode && isOpen && mirrorEl && !e.target?.closest?.(".livecode-header, .cue-text-overlay, .oscilla-shape[data-drag]");

function onPerformPointerDown(e) {
  if (!ownsPointer(e) || e.button !== 0 || !inCodeColumn(e.clientX, e.clientY)) return;
  if (e.altKey) {                          // Alt+drag: the whole block
    e.preventDefault();
    e.stopPropagation();
    const now = performance.now();
    if (now - _lastDown.t < 400) { setCodeOffset(0, 0); saveSession(); _lastDown = { t: 0, x: 0, y: 0 }; return; }
    _lastDown = { t: now, x: e.clientX, y: e.clientY };
    startMove(e);
    return;
  }
  pointing(true);
  const at = textOffsetAt(e.clientX, e.clientY);
  if (at == null) { pointing(false); return; }
  e.preventDefault();                      // no text selection in the layer itself
  e.stopPropagation();
  editorEl.focus({ preventScroll: true });
  _anchor = e.shiftKey ? (editorEl.selectionDirection === "backward" ? editorEl.selectionEnd : editorEl.selectionStart) : at;
  const now = performance.now();
  const double = now - _lastDown.t < 400 && Math.hypot(e.clientX - _lastDown.x, e.clientY - _lastDown.y) < 6;
  _lastDown = double ? { t: 0, x: 0, y: 0 } : { t: now, x: e.clientX, y: e.clientY };
  if (double) {                            // double click: the word
    const text = editorEl.value, word = /[\w.#-]/;
    let a = at, b = at;
    while (a > 0 && word.test(text[a - 1])) a--;
    while (b < text.length && word.test(text[b])) b++;
    editorEl.setSelectionRange(a, b);
    pointing(false);
    _anchor = null;
    return;
  }
  editorEl.setSelectionRange(Math.min(_anchor, at), Math.max(_anchor, at));
  window.addEventListener("pointermove", onPerformPointerMove, true);
  window.addEventListener("pointerup", onPerformPointerUp, true);
}

function onPerformPointerMove(e) {
  if (_anchor == null) return;
  const at = textOffsetAt(e.clientX, e.clientY);
  if (at == null) return;
  editorEl.setSelectionRange(Math.min(_anchor, at), Math.max(_anchor, at), at < _anchor ? "backward" : "forward");
}

function onPerformPointerUp() {
  _anchor = null;
  pointing(false);
  window.removeEventListener("pointermove", onPerformPointerMove, true);
  window.removeEventListener("pointerup", onPerformPointerUp, true);
}

/**
 * A swipe begun over the code scrolls the code to its end, wherever it
 * drifts. A pinch over it (the browser sends ctrl+wheel) sizes the code,
 * smoothly; ctrl+wheel anywhere else is still the score's zoom.
 */
function onPerformWheel(e) {
  if (!ownsPointer(e)) return;
  const now = performance.now();
  if (now > _wheelUntil && !inCodeColumn(e.clientX, e.clientY)) return;
  _wheelUntil = now + WHEEL_LATCH_MS;
  e.preventDefault();
  e.stopPropagation();
  if (e.ctrlKey) {
    setFontSize(currentFontSize() * Math.exp(-e.deltaY * 0.01));
    return;
  }
  editorEl.scrollTop += e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
  syncMirrorScroll();
}

// Alt+drag in the code moves the whole block (perform mode); Alt+double
// click puts it back. Kept with the session.
let _move = null;

function setCodeOffset(dx, dy) {
  panelEl.style.setProperty("--lc-dx", `${Math.round(dx)}px`);
  panelEl.style.setProperty("--lc-dy", `${Math.round(dy)}px`);
  sendViewSoon();
}
const codeOffset = () => [
  parseFloat(panelEl.style.getPropertyValue("--lc-dx")) || 0,
  parseFloat(panelEl.style.getPropertyValue("--lc-dy")) || 0,
];

function startMove(e) {
  const [dx, dy] = codeOffset();
  _move = { x: e.clientX, y: e.clientY, dx, dy };
  const move = (ev) => setCodeOffset(_move.dx + ev.clientX - _move.x, _move.dy + ev.clientY - _move.y);
  const up = () => {
    _move = null;
    window.removeEventListener("pointermove", move, true);
    window.removeEventListener("pointerup", up, true);
    saveSession();
  };
  window.addEventListener("pointermove", move, true);
  window.addEventListener("pointerup", up, true);
}

/** On while perform mode is on and the console shown. */
function performPointer(on) {
  const f = on ? "addEventListener" : "removeEventListener";
  window[f]("pointerdown", onPerformPointerDown, true);
  window[f]("wheel", onPerformWheel, { capture: true, passive: false });
  if (!on) onPerformPointerUp();
}

/** Something that takes typing of its own: a field, a dialog's input. */
const isTextEntry = el =>
  !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

/** After a click on the score, typing goes back to the code -- unless the
 *  click gave focus to something that takes typing (an input, a dialog).
 *  A button or a link in the score does not keep the keyboard. */
function refocusEditor(e) {
  if (panelEl?.contains(e.target)) return;
  setTimeout(() => {
    if (!performMode || !isOpen) return;
    const a = document.activeElement;
    if (a === editorEl || isTextEntry(a)) return;
    editorEl?.focus({ preventScroll: true });
  }, 0);
}

/**
 * In perform mode the keyboard belongs to the code. A key pressed while the
 * focus is somewhere else -- a score button that was clicked -- goes to the
 * editor, the key itself included (focus moved during keydown takes the
 * character), and nowhere else: an arrow typed into the code must not move
 * the playhead. Called first by keyboardShortcuts (setKeyboardOwner).
 * Ctrl / Alt / Meta combinations pass: the console's own shortcuts.
 */
function keysToEditor(e) {
  if (!performMode || !isOpen || !editorEl) return false;
  if (e.ctrlKey || e.altKey || e.metaKey) return false;
  const a = document.activeElement;
  if (a === editorEl || isTextEntry(a)) return false;
  if (["Control", "Shift", "Alt", "Meta"].includes(e.key)) return false;
  editorEl.focus({ preventScroll: true });
  e.stopPropagation();
  return true;
}

/** Ctrl+Shift+D: the cue under the caret's page in the docs, in a new tab (liveDocs.js). */
function openHelpAtCaret() {
  if (!editorEl) return;
  const text = editorEl.value, pos = editorEl.selectionStart;
  // within the expression the caret is in (a bracket left open lines above is not it)
  const { before } = expressionAtCaret(text, pos);
  const after = text.slice(pos).split("\n")[0];
  const url = helpUrl(before + after, before.length);
  window.open(url, "_blank", "noopener");
  if (outputEl) {
    const line = document.createElement("div");
    line.className = "livecode-output-line";
    line.textContent = `help: ${url.replace(/^\/oscilla\/docs\//, "")}`;
    appendOutput(line);
  }
}

/** Ctrl+. -- through the cue pipeline, so a ?view=live window hushes the score window. */
function hush() {
  runExpression("hush()");
}

/**
 * Ctrl+., Ctrl+, and Ctrl+Shift+, ask first: a small box -- Enter does it
 * (every sound killed; every text and animation stopped; and the screen
 * plain again), Esc (or a few seconds) leaves
 * it. The same key again while it shows does it at once, so the panicked
 * double tap still works. Keys are taken before the editor sees them.
 */
let hushAsk = null;                    // { kind, box, onKey, timer }
const HUSH_ASK_MS = 4000;
const ASKS = {
  hush: { text: "Kill all sound?", act: () => hush(), verb: "kill" },
  still: { text: "Stop all texts and animation?", act: () => still(), verb: "stop" },
  reset: { text: "Screen back to plain? (texts, animation, screen(), every viz's effects)", act: () => runExpression("still(all)"), verb: "reset" },
};
function askFirst(kind) {
  if (hushAsk?.kind === kind) { closeHushAsk(); ASKS[kind].act(); return; }   // the key twice: now
  closeHushAsk();
  const { text, act, verb } = ASKS[kind];
  const box = document.createElement("div");
  box.className = "lc-hush-ask";
  box.setAttribute("role", "alertdialog");
  box.innerHTML = `<b>${text}</b> <span><kbd>Enter</kbd> ${verb} · <kbd>Esc</kbd> dismiss</span>`;
  document.body.appendChild(box);
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); e.stopImmediatePropagation(); closeHushAsk(); act(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); closeHushAsk(); }
  };
  window.addEventListener("keydown", onKey, true);
  hushAsk = { kind, box, onKey, timer: setTimeout(closeHushAsk, HUSH_ASK_MS) };
}
const askHush = () => askFirst("hush");
function closeHushAsk() {
  if (!hushAsk) return;
  clearTimeout(hushAsk.timer);
  window.removeEventListener("keydown", hushAsk.onKey, true);
  hushAsk.box.remove();
  hushAsk = null;
}

/** Ctrl+, -- still(): what moves on the screen stops (cues/still.js); the sound plays on. */
function still() {
  runExpression("still()");
}


// =================================================================
//  FILL ALL ANIMS
// =================================================================

function fillAllAnims() {
  if (!editorEl) return;
  const reg = window.oscillaAnimRegistry;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const lines = [];
  for (const [uid, record] of Object.entries(reg)) {
    const el = record.el;
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    // Skip elements entirely outside the viewport
    if (rect.right < 0 || rect.bottom < 0 || rect.left > vw || rect.top > vh) continue;
    const dsl = el.getAttribute?.("data-oscilla") || (el.id?.includes("(") ? el.id : null);
    lines.push(uid + " > " + (dsl ? dsl.replace(/\s+/g, " ").trim() : ""));
  }
  if (!lines.length) {
    logOutput("no visible animations in viewport", "info");
    return;
  }
  editorEl.focus();
  editorEl.select();
  insertText(lines.join("\n"));   // undoable: one Ctrl+Z brings the old text back
  logOutput(`loaded ${lines.length} visible animation(s)`, "info");
}


// =================================================================
//  PANEL VISIBILITY SETTINGS
// =================================================================

function toggleSettingsPanel() {
  const settingsEl = panelEl?.querySelector('[data-role="settings"]');
  if (!settingsEl) return;
  settingsEl.style.display = settingsEl.style.display === "none" ? "flex" : "none";
}

function applyPanelVisibility() {
  if (!panelEl) return;

  // Gather visibility state from checkboxes
  const visibility = {};
  const checkboxes = panelEl.querySelectorAll('[data-role="settings"] input[type="checkbox"]');
  checkboxes.forEach(cb => {
    visibility[cb.dataset.panel] = cb.checked;
  });

  // Apply visibility to sections
  for (const [sectionName, isVisible] of Object.entries(visibility)) {
    // Special case: buffers (tab bar)
    if (sectionName === "buffers") {
      const tabBar = panelEl.querySelector(".livecode-tab-bar");
      if (tabBar) tabBar.style.display = isVisible ? "" : "none";
      continue;
    }

    // Find all elements with this data-section
    const sections = panelEl.querySelectorAll(`[data-section="${sectionName}"]`);
    sections.forEach(section => {
      section.style.display = isVisible ? "" : "none";
    });
  }

  // Handle resize bars: only show if BOTH adjacent panels are visible
  const resizeBars = panelEl.querySelectorAll(".livecode-resize-bar");
  resizeBars.forEach(bar => {
    const resizeType = bar.dataset.resize; // e.g., "editor-chat", "chat-monitor", "monitor-output"
    if (!resizeType) return;

    const [panelA, panelB] = resizeType.split("-");
    const bothVisible = visibility[panelA] && visibility[panelB];
    bar.style.display = bothVisible ? "" : "none";
  });
}

async function loadPanelPreferences() {
  if (!panelEl) return;

  const projectName = window.currentProjectName;
  if (!projectName) return;

  try {
    const res = await fetch(`/scores/${projectName}/preferences.json`);
    if (!res.ok) return;

    const prefs = await res.json();

    // Apply saved visibility to checkboxes (using flat keys like liveConsoleMonitor)
    const checkboxes = panelEl.querySelectorAll('[data-role="settings"] input[type="checkbox"]');
    checkboxes.forEach(cb => {
      const sectionName = cb.dataset.panel;
      const prefKey = `liveConsole${sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}`;
      if (prefKey in prefs) {
        cb.checked = prefs[prefKey];
      }
    });

    applyPanelVisibility();
  } catch (e) {
    console.warn("[Live] Could not load panel preferences:", e);
  }
}

async function savePanelPreferences() {
  const projectName = window.currentProjectName;
  if (!projectName || !panelEl) return;

  // Gather current checkbox states with flat keys (liveConsoleMonitor, etc.)
  const panelPrefs = {};
  const checkboxes = panelEl.querySelectorAll('[data-role="settings"] input[type="checkbox"]');
  checkboxes.forEach(cb => {
    const sectionName = cb.dataset.panel;
    const prefKey = `liveConsole${sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}`;
    panelPrefs[prefKey] = cb.checked;
  });

  try {
    // Load existing preferences
    let prefs = {};
    const res = await fetch(`/scores/${projectName}/preferences.json`);
    if (res.ok) prefs = await res.json();

    // Merge in panel preferences
    Object.assign(prefs, panelPrefs);

    // Save back
    await fetch(`/save-preferences/${encodeURIComponent(projectName)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(prefs)
    });
  } catch (e) {
    console.warn("[Live] Could not save panel preferences:", e);
  }
}


// =================================================================
//  BUILD PANEL
// =================================================================

function buildPanel() {
  panelEl = document.createElement("div");
  panelEl.id = "livecode-panel";

  panelEl.innerHTML = `
    <!-- Header (drag handle) -->
    <div class="livecode-header">
      <span class="livecode-header-title" title="Oscilla Live -- live coding in Oscilla">oscilive</span>
      <button class="livecode-font-btn" data-action="font-down" title="Decrease font size">A−</button>
      <button class="livecode-font-btn" data-action="font-up"   title="Increase font size">A+</button>
      <button class="livecode-font-btn" data-action="perform" title="Perform: code over the whole score (ctrl+shift+f)">⛶</button>
      <button class="livecode-settings-btn" data-action="toggle-settings" title="Panel Settings">⚙</button>
      <button class="livecode-close" data-action="close">&times;</button>
    </div>

    <!-- Settings panel (collapsible) -->
    <div class="livecode-settings" data-role="settings" style="display:none">
      <label><input type="checkbox" data-panel="target" checked /> Target</label>
      <label><input type="checkbox" data-panel="buffers" checked /> Buffers</label>
      <label><input type="checkbox" data-panel="editor" checked /> Editor</label>
      <label><input type="checkbox" data-panel="chat" checked /> Chat</label>
      <label><input type="checkbox" data-panel="monitor" checked /> Monitor</label>
      <label><input type="checkbox" data-panel="output" checked /> Output</label>
    </div>

    <!-- Target selector -->
    <div class="livecode-target" data-section="target">
      <span class="livecode-target-label">target</span>
      <input class="livecode-target-input"
             placeholder="uid or element id"
             spellcheck="false"
             list="livecode-element-list"
             data-role="target-input" />
      <datalist id="livecode-element-list"></datalist>
      <button class="livecode-pick-btn" data-action="pick">pick</button>
      <button class="livecode-pick-btn" data-action="fill-all" title="Fill editor with all active animation DSL lines">all anims</button>
    </div>
    <div class="livecode-target-info" data-role="target-info" data-section="target"></div>

    <!-- Editor -->
    <div class="livecode-editor-wrap" data-section="editor">
      <div class="livecode-code">
        <pre class="livecode-mirror" data-role="mirror" aria-hidden="true"></pre>
        <textarea class="livecode-editor"
                  placeholder="type DSL here...  e.g. synth(uid:a, freq:220)"
                  spellcheck="false"
                  data-role="editor"></textarea>
      </div>
      <div class="livecode-actions">
        <button class="livecode-run-btn" data-action="run-line">
          run line <kbd>ctrl+enter</kbd>
        </button>
        <button class="livecode-run-btn" data-action="run-all">
          run all <kbd>ctrl+alt+shift+enter</kbd>
        </button>
      </div>
    </div>

    <!-- Resize bar: editor | chat -->
    <div class="livecode-resize-bar" data-resize="editor-chat"></div>

    <!-- Chat -->
    <div class="livecode-chat-wrap" data-section="chat">
      <div class="livecode-chat-messages" data-role="chat"></div>
      <div class="livecode-chat-input-wrap">
        <input class="livecode-chat-input"
               placeholder="message..."
               spellcheck="false"
               data-role="chat-input" />
      </div>
    </div>

    <!-- Resize bar: chat | monitor -->
    <div class="livecode-resize-bar" data-resize="chat-monitor"></div>

    <!-- Monitor (tabbed: osc / signals) -->
    <div class="livecode-monitor-wrap" data-section="monitor">
      <div class="livecode-monitor-header">
        <button class="livecode-monitor-tab active" data-monitor-tab="osc">osc</button>
        <button class="livecode-monitor-tab" data-monitor-tab="signals">signals</button>
        <input class="livecode-monitor-filter"
               placeholder="filter..."
               spellcheck="false"
               data-role="monitor-filter" />
        <button class="livecode-monitor-clear" data-action="clear-monitor">clear</button>
      </div>
      <div class="livecode-oscmon" data-role="oscmon"></div>
      <div class="livecode-signals" data-role="signals" style="display:none"></div>
    </div>

    <!-- Resize bar: monitor | output -->
    <div class="livecode-resize-bar" data-resize="monitor-output"></div>

    <!-- Output log -->
    <div class="livecode-output-wrap" data-section="output">
      <div class="livecode-output-header">
        output
        <button class="livecode-output-clear" data-action="clear-output">clear</button>
      </div>
      <div class="livecode-output" data-role="output"></div>
    </div>
  `;

  document.body.appendChild(panelEl);

  // Cache refs
  editorEl       = panelEl.querySelector('[data-role="editor"]');
  mirrorEl       = panelEl.querySelector('[data-role="mirror"]');
  outputEl       = panelEl.querySelector('[data-role="output"]');
  signalsEl      = panelEl.querySelector('[data-role="signals"]');
  targetInputEl  = panelEl.querySelector('[data-role="target-input"]');
  targetInfoEl   = panelEl.querySelector('[data-role="target-info"]');
  pickBtnEl      = panelEl.querySelector('[data-action="pick"]');
  signalFilterEl = panelEl.querySelector('[data-role="monitor-filter"]');
  oscMonitorEl   = panelEl.querySelector('[data-role="oscmon"]');
  oscMonitorFilterEl = signalFilterEl;  // shared filter input
  chatEl         = panelEl.querySelector('[data-role="chat"]');
  chatInputEl    = panelEl.querySelector('[data-role="chat-input"]');

  // Wire events
  panelEl.querySelector('[data-action="close"]')
    .addEventListener("click", closePanel);

  panelEl.querySelector('[data-action="pick"]')
    .addEventListener("click", togglePicking);

  panelEl.querySelector('[data-action="fill-all"]')
    .addEventListener("click", fillAllAnims);

  panelEl.querySelector('[data-action="font-down"]')
    .addEventListener("click", () => adjustFontSize(-1));

  panelEl.querySelector('[data-action="font-up"]')
    .addEventListener("click", () => adjustFontSize(+1));

  panelEl.querySelector('[data-action="perform"]')
    .addEventListener("click", () => setPerformMode(!performMode));

  // Save cursor position on mousedown (before the button click steals focus from the textarea)
  let _savedCursorPos = null;
  const runLineBtn = panelEl.querySelector('[data-action="run-line"]');
  runLineBtn.addEventListener("mousedown", () => {
    if (editorEl) _savedCursorPos = editorEl.selectionStart;
  });
  runLineBtn.addEventListener("click", () => {
    runAtCursor(_savedCursorPos);
    _savedCursorPos = null;
  });

  panelEl.querySelector('[data-action="run-all"]')
    .addEventListener("click", executeAll);

  panelEl.querySelector('[data-action="clear-output"]')
    .addEventListener("click", clearOutput);

  panelEl.querySelector('[data-action="clear-monitor"]')
    .addEventListener("click", clearActiveMonitor);

  // Settings toggle
  panelEl.querySelector('[data-action="toggle-settings"]')
    .addEventListener("click", toggleSettingsPanel);

  // Panel visibility checkboxes
  panelEl.querySelectorAll('[data-role="settings"] input[type="checkbox"]').forEach(cb => {
    cb.addEventListener("change", () => {
      applyPanelVisibility();
      savePanelPreferences();
    });
  });

  // Monitor tab switching
  panelEl.querySelectorAll("[data-monitor-tab]").forEach(btn => {
    btn.addEventListener("click", () => switchMonitorTab(btn.dataset.monitorTab));
  });

  // Chat input: send on Enter
  chatInputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const msg = chatInputEl.value.trim();
      if (msg) {
        sendChatMessage(msg);
        chatInputEl.value = "";
      }
    }
  });

  // Target input: resolve on Enter
  targetInputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      resolveTarget(targetInputEl.value.trim());
    }
  });

  // Editor keyboard shortcuts (Ctrl+Enter and its Shift / Alt forms are handled at document level)
  editorEl.addEventListener("copy", onLineCopy);
  editorEl.addEventListener("cut", onLineCopy);
  editorEl.addEventListener("paste", onLinePaste);

  editorEl.addEventListener("keydown", (e) => {
    // Ctrl+Shift+B: the block at the caret alone, then its line, then all the code again
    if (e.ctrlKey && e.shiftKey && !e.altKey && e.code === "KeyB") {
      e.preventDefault();
      toggleFocusMode();
      return;
    }
    // Ctrl+Shift+E: jump to cue browser (Ctrl+J is the browser's
    // downloads, Ctrl+Shift+J its dev tools)
    if (e.ctrlKey && e.shiftKey && e.code === "KeyE") {
      e.preventDefault();
      enterCueBrowse();
    }
    // the suggestions list, while open, has the arrows, Tab, Enter and Esc
    if (_suggestList && handleListKey(e)) return;
    if (e.key === "Escape" && cheatOpen()) { e.preventDefault(); toggleCheatsheet(false); return; }
    // Ctrl+Shift+S: copy the line, or the selected lines, to the set's
    // stash; with Alt, to the global one
    if (e.ctrlKey && e.shiftKey && e.code === "KeyS") {
      e.preventDefault();
      stashAtCursor(e.altKey);
      return;
    }
    // Ctrl+Shift+/: comment the line (or every line the selection touches) out, or back in.
    // By key and by character: on a layout where / is Shift+7 (German,
    // Slovene...) the US slash key types something else
    if (e.ctrlKey && e.shiftKey && (e.code === "Slash" || e.key === "/")) {
      e.preventDefault();
      toggleComment();
      return;
    }
    // Ctrl+Shift+K: delete the line, or every line the selection touches
    if (e.ctrlKey && e.shiftKey && e.code === "KeyK") {
      e.preventDefault();
      deleteLines();
      return;
    }
    // Ctrl+Space: the list of suggestions
    if (e.ctrlKey && e.code === "Space") {
      e.preventDefault();
      openSuggestList();
      return;
    }
    // Alt+Enter on a scene's #$ line: to that scene, fading over four beats (liveScenes.js)
    if (e.key === "Enter" && e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey) {
      const line = editorEl.value.slice(0, editorEl.selectionStart).split("\n").length - 1;
      const name = sceneNameOf(editorEl.value.split("\n")[line] ?? "");
      if (name) { e.preventDefault(); runExpression({ expr: `scene(${name}, fade:4)` }); return; }
    }
    // Enter inside an open bracket: the next line carries the expression on,
    // indented one step (two spaces, as Tab) past where it began
    if (e.key === "Enter" && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey
        && editorEl.selectionStart === editorEl.selectionEnd) {
      const { depth, indent } = expressionAtCaret(editorEl.value, editorEl.selectionStart);
      if (depth > 0) {
        e.preventDefault();
        insertText("\n" + indent + "  ");
        return;
      }
    }
    // Tab: on a #$ head, a snapshot offered there taken (#$ s1 ca|lm), else
    // fold or unfold its block; else take the ghost suggestion, or insert two spaces
    const onHead = () => isFoldHead(editorEl.value.split("\n")[caretLine()] ?? "");
    if (e.key === "Tab" && !e.shiftKey && ((onHead() && takeGhost()) || toggleFoldAtCaret())) {
      e.preventDefault();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      if (!takeGhost()) insertText("  ");
    }
  });

  // Debounced autosave + network send on editor input
  editorEl.addEventListener("scroll", syncMirrorScroll);
  document.addEventListener("selectionchange", onSelectionChange);
  editorEl.addEventListener("focus", onSelectionChange);
  editorEl.addEventListener("blur", onSelectionChange);
  editorEl.addEventListener("blur", () => setTimeout(closeSuggestList, 150));
  editorEl.addEventListener("input", (e) => {
    if (_inserting) { _inserting.n++; return; }   // insertText(): once, when it is all in
    onEditorInput(e.inputType);
  });

  // Prevent panel interactions from bubbling into the score/transport
  panelEl.addEventListener("mousedown", (e) => e.stopPropagation());
  panelEl.addEventListener("click", (e) => e.stopPropagation());
  panelEl.addEventListener("keydown", (e) => e.stopPropagation());
  panelEl.addEventListener("keyup", (e) => e.stopPropagation());
  panelEl.addEventListener("keypress", (e) => e.stopPropagation());

  // Document-level capture handler so Ctrl+Enter works even when textarea has lost focus
  // (e.g. right after clicking the "run line" button)
  const onCtrlKeys = (e) => {
    if (!panelEl || panelEl.style.display === "none") return;
    if (!e.ctrlKey) return;
    // Ctrl+= / Ctrl+- -- the code's size (the panel's or perform mode's).
    // The browser's page zoom keys: taken while the console is open.
    // By character and by key, so + and - work on any layout and the numpad.
    if (e.key === "=" || e.key === "+" || e.code === "Equal" || e.code === "NumpadAdd") {
      e.preventDefault(); e.stopPropagation();
      adjustFontSize(+1);
      return;
    }
    // Ctrl+_ (Ctrl+Shift+-) -- the line's synths frozen on their last note, or going on
    if (e.key === "_" || (e.shiftKey && e.code === "Minus")) {
      e.preventDefault(); e.stopPropagation();
      freezeAtCursor();
      return;
    }
    if (e.key === "-" || e.code === "Minus" || e.code === "NumpadSubtract") {
      e.preventDefault(); e.stopPropagation();
      adjustFontSize(-1);
      return;
    }
    // Ctrl+Shift+. -- take back what the line under the cursor started
    if (e.shiftKey && e.code === "Period") {
      e.preventDefault(); e.stopPropagation();
      stopLineAtCursor();
      return;
    }
    // Ctrl+. -- hush: every sound stops, wherever focus is -- after a
    // word: Enter to kill, Esc to leave it (Ctrl+. twice: at once)
    if (e.key === ".") {
      e.preventDefault(); e.stopPropagation();
      askHush();
      return;
    }
    // Ctrl+Shift+D -- the help page of the cue under the caret, in a new tab
    if (e.shiftKey && e.code === "KeyD") {
      e.preventDefault(); e.stopPropagation();
      openHelpAtCaret();
      return;
    }
    // Ctrl+Shift+, -- still(all): that, and the screen back to plain --
    // screen(off), every viz()'s effects off. By key: Shift+, is < or ; by layout
    if (e.shiftKey && e.code === "Comma") {
      e.preventDefault(); e.stopPropagation();
      askFirst("reset");
      return;
    }
    // Ctrl+, -- still: every animation and text stops, the sound plays on --
    // after a word: Enter to stop, Esc to leave it (Ctrl+, twice: at once)
    if (e.key === ",") {
      e.preventDefault(); e.stopPropagation();
      askFirst("still");
      return;
    }
    // Ctrl+Shift+P -- the code on the projector window, or not
    if (e.shiftKey && e.code === "KeyP") {
      e.preventDefault(); e.stopPropagation();
      toggleProjectorCode();
      return;
    }
    // Ctrl+Shift+M -- map:'s handles shown, or hidden for the show
    if (e.shiftKey && e.code === "KeyM") {
      e.preventDefault(); e.stopPropagation();
      mapHandlesCue(handlesShown() ? "hide" : "show");
      return;
    }
    // Ctrl+Shift+H -- the cheatsheet, faint over the code
    if (e.shiftKey && e.code === "KeyH") {
      e.preventDefault(); e.stopPropagation();
      toggleCheatsheet();
      return;
    }
    // Ctrl+Shift+F -- perform mode on/off
    if (e.shiftKey && (e.key === "F" || e.key === "f")) {
      e.preventDefault(); e.stopPropagation();
      setPerformMode(!performMode);
      return;
    }
    if (e.key !== "Enter") return;
    // Another text field (chat, the DSL inspector) keeps its own Ctrl+Enter.
    const a = document.activeElement;
    if (a !== editorEl && isTextEntry(a)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey) {
      // Ctrl+Shift+Enter: run everything, folded blocks too (with Alt as well: the same)
      executeAll();
    } else if (e.altKey) {
      // Ctrl+Alt+Enter: a poly: sound's voices all take the line
      runAllVoices = true;
      try { runAtCursor(); } finally { runAllVoices = false; }
    } else {
      runAtCursor();
    }
  };
  document.addEventListener("keydown", onCtrlKeys, true);

  // --- Tab bar (inserted before the editor section) ---
  const editorWrap = panelEl.querySelector('[data-section="editor"]');
  if (editorWrap) {
    buildTabBar();
    editorWrap.parentNode.insertBefore(tabBarEl, editorWrap);
  }

  // --- Restore buffer from localStorage ---
  restoreLocalBuffer();

  // --- Draggable by header, resizable edges and section bars ---
  makeDraggable(panelEl, panelEl.querySelector(".livecode-header"));
  initSectionResize(panelEl);
  initPanelResize(panelEl);

  // --- Dedicated view: fetch element list, disable pick ---
  if (isDedicatedView()) {
    if (pickBtnEl) {
      pickBtnEl.disabled = true;
      pickBtnEl.textContent = "n/a";
      pickBtnEl.title = "Pick unavailable -- use the dropdown list";
    }
    fetchScoreElements();

    // In dedicated view, selecting from datalist sets the remote target
    targetInputEl.addEventListener("input", () => {
      const val = targetInputEl.value.trim();
      if (val) {
        selectedTargetId = val;
        if (targetInfoEl) targetInfoEl.textContent = val;
        logOutput("target: " + val, "info");
      }
    });
  }
}


// =================================================================
//  REMOTE ELEMENT LIST (dedicated view)
//  Fetches the project SVG via HTTP and extracts element IDs
//  to populate the target datalist -- no rendering, no cue init.
// =================================================================

async function fetchScoreElements() {
  const project = new URLSearchParams(window.location.search).get("project");
  if (!project) {
    logOutput("no ?project= in URL -- cannot fetch elements", "err");
    return;
  }

  // Inkscape internal IDs to skip
  const SKIP_RE = /^(defs|metadata|namedview|base|layer|sodipodi|inkscape)\d*$/i;

  try {
    const res = await fetch(`/scores/${encodeURIComponent(project)}/score.svg`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const svgText = await res.text();

    const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
    const datalist = panelEl.querySelector("#livecode-element-list");
    if (!datalist) return;

    datalist.innerHTML = "";

    const targetIds = [];
    const cueIds = [];

    for (const el of doc.querySelectorAll("[id], [data-oscilla]")) {
      // Normalize: Inkscape's XML editor may embed newlines in long IDs
      const id = (el.getAttribute("id") || "").replace(/\s+/g, " ").trim();
      if (id && !SKIP_RE.test(id)) targetIds.push(id);

      // A cue is whatever the parser accepts, in the id or in data-oscilla
      for (const raw of [id, el.getAttribute("data-oscilla")]) {
        const dsl = (raw || "").replace(/\s+/g, " ").trim();
        if (dsl.includes("(") && !cueIds.includes(dsl) && !checkExpression(dsl).error) cueIds.push(dsl);
      }
    }

    // Populate datalist for target autocomplete
    for (const id of targetIds) {
      const opt = document.createElement("option");
      opt.value = id;
      datalist.appendChild(opt);
    }

    // Log summary
    logOutput(`${targetIds.length} elements, ${cueIds.length} cues in ${project}`, "info");

    // Store for keyboard browsing
    scoreCueIds = cueIds;

    // Render cue entries as selectable items
    for (let i = 0; i < cueIds.length; i++) {
      const id = cueIds[i];
      const display = id.length > 80 ? id.slice(0, 77) + "..." : id;

      const line = document.createElement("div");
      line.className = "livecode-output-line livecode-cue-entry";
      line.textContent = display;
      line.dataset.cueIdx = i;
      line.dataset.fullCue = id;

      // Click to insert
      line.addEventListener("click", () => insertCueAtCursor(id));

      outputEl.appendChild(line);
    }

    if (cueIds.length) {
      logOutput("ctrl+shift+e to browse cues, click to insert", "info");
    }

  } catch (err) {
    logOutput("fetch score failed: " + err.message, "err");
  }
}


// =================================================================
//  EDITOR HELPERS
// =================================================================

/**
 * Ctrl+Enter / run line. On code: run the expression the cursor is in.
 * On commented-out code: silence the sounds it names -- comment a block
 * out and run it, and it stops.
 */
function runAtCursor(pos = null) {
  if (!editorEl) return;
  const at = pos != null ? pos : editorEl.selectionStart;
  const line = editorEl.value.slice(0, at).split("\n").length - 1;
  // on a #$ head: its block, folded or not
  if (isFoldHead(editorEl.value.split("\n")[line] ?? "")) {
    const b = blockAtHead(line);
    if (!b.exprs.length) { logOutput("nothing in this block to run", "info"); return; }
    // in turn: a wait(16) puts what follows 16 beats later; a level() fading in
    // runs before its sound starts, within its stretch (runEntriesInTurn)
    const key = `block:${editorEl.value.split("\n")[line].trim()}`;
    runEntriesInTurn(b.exprs, (e, now) => {
      if (b.folded || !now) {
        const ran = runExpression({ expr: e.expr });
        if (b.folded && ran != null) flashLines(line, line, ran ? "ok" : "err");
      } else executeLine(expressionAtLine(editorEl.value, e.startLine) || e);
    }, key);
    return;
  }
  const entry = expressionAtLine(editorEl.value, line);
  if (entry && trigSwitchOff(entry)) return;
  if (entry) { if (executeLine(entry)) flipSwitch(entry.startLine); return; }
  // commented-out code: take back what it started
  const code = commentedCode(editorEl.value, line);
  if (code) takeBack(scanExpressions(code).map(e => e.expr), line, line);
}

/**
 * Ctrl+Enter on a line running on a signal, unchanged since it was run: it
 * stops watching (trig:off) or following (follow(...) let go, a screen()
 * back to 0), and the signal itself -- the beat, a sound -- goes on. The next Ctrl+Enter starts it again. Edited, the line runs as
 * usual and replaces the one watching. true: switched off.
 */
function trigSwitchOff(entry) {
  const { target, watching } = trigWatchers(entry.expr);
  const trigsOn = watching.length && watching.every(w => w.watcher.source === w.part.expr);
  const following = boundParts(entry.expr);
  if (!trigsOn && !following.length) return false;
  let ran = true;
  if (trigsOn) {
    const off = watching.map(({ part }) => `${target ? `${target} > ` : ""}${part.expr.replace(/\btrig\s*:\s*("[^"]*"|'[^']*'|every\s*\([^)]*\)|[^,)\s]+)/, "trig:off")}`);
    ran = off.map(e => runExpression(e)).every(Boolean);
  }
  // follow(...): let go here and on the projector, each back to its rest
  if (following.length) {
    for (const src of following) stopBindingsOf(src);
    mirrorRun({ unfollow: following });
    logOutput(`${entry.expr.replace(/\s+/g, " ").trim()}  -- off`, "info");
  }
  flashLines(entry.startLine, entry.endLine, ran ? "ok" : "err");
  return true;
}

/**
 * hold(s, 1) run with Ctrl+Enter: its line becomes hold(s, 0), gate(s, 0)
 * gate(s, 1) -- the next press does the other thing (liveEval flipToggle).
 * Undoable; the caret stays where it was in the line.
 */
function flipSwitch(startLine) {
  if (!editorEl || editorEl.readOnly || activeTab !== "local") return;
  const entry = expressionAtLine(editorEl.value, startLine);
  if (!entry || entry.startLine !== entry.endLine) return;
  const lines = editorEl.value.split("\n");
  const text = lines[startLine];
  const flipped = flipToggle(text);
  if (flipped == null || flipped === text) return;
  const from = lines.slice(0, startLine).reduce((n, l) => n + l.length + 1, 0);
  const caret = editorEl.selectionStart;
  editorEl.setSelectionRange(from, from + text.length);
  insertText(flipped);
  const at = caret > from + text.length ? caret + flipped.length - text.length : Math.min(caret, from + flipped.length);
  editorEl.setSelectionRange(at, at);
}

/**
 * Ctrl+Shift+. -- take back what the line under the cursor started, without
 * touching its text: Ctrl+Enter brings it back. (On commented-out code it is
 * the same as running it.)
 */
function stopLineAtCursor() {
  if (!editorEl) return;
  const pos = editorEl.selectionStart;
  const line = editorEl.value.slice(0, pos).split("\n").length - 1;
  if (isFoldHead(editorEl.value.split("\n")[line] ?? "")) {
    takeBack(blockAtHead(line).exprs.map(e => e.expr), line, line);
    return;
  }
  const entry = expressionAtLine(editorEl.value, line);
  if (entry) { takeBack([entry.expr], entry.startLine, entry.endLine); return; }
  const code = commentedCode(editorEl.value, line);
  if (code) takeBack(scanExpressions(code).map(e => e.expr), line, line);
}

/**
 * Ctrl+_ : the synths of the line under the cursor (a fold head:
 * its block's) frozen on what their patterns drew last -- hold() -- and,
 * pressed again, going on from there. Their lines show it (held).
 */
function freezeAtCursor() {
  if (!editorEl) return;
  const pos = editorEl.selectionStart;
  const text = editorEl.value;
  const line = text.slice(0, pos).split("\n").length - 1;
  let exprs = [], from = line, to = line;
  if (isFoldHead(text.split("\n")[line] ?? "")) exprs = blockAtHead(line).exprs.map(e => e.expr);
  else {
    const entry = expressionAtLine(text, line);
    if (entry) { exprs = [entry.expr]; from = entry.startLine; to = entry.endLine; }
  }
  // its synths, and its animations: bass > scale(uid:bsc, ...) stops where it is
  const names = [...new Set([...exprs.flatMap(e => synthsOf(e)), ...animUidsOf(exprs)])];
  if (!names.length) { logOutput("nothing on this line to freeze -- a synth, or an animation", "info"); return; }
  const held = new Set([...soundStates().held, ...heldAnims()]);
  const thaw = names.every(n => held.has(n));
  const ran = runExpression(`hold([${names.join(", ")}], ${thaw ? 0 : 1})`);
  flashLines(from, to, ran ? "ok" : "err");
  logOutput(`${names.join(", ")} ${thaw ? "going on" : "frozen -- Ctrl+_ again to go on"}`, "info");
}

/**
 * The animations these lines started, by their uids in the registry: a
 * uid: given, else those of the line's kind on its target (bass >
 * scale(...) -- the scale on bass, not one on a group round it).
 */
function animUidsOf(exprs) {
  const reg = window.oscillaAnimRegistry;
  const out = [];
  for (const e of exprs) {
    const { target, anims } = animsOf(e);
    if (!anims.length) continue;
    const el = target ? findTargetElement(target) : null;
    for (const a of anims) {
      if (a.uid && reg[a.uid]) { out.push(a.uid); continue; }
      if (!el) continue;
      // rotate(dur:4) with no values: registered as rotate-fallback (its continuous form)
      for (const [u, r] of Object.entries(reg)) {
        if ((r.kind === a.kind || String(r.kind).startsWith(`${a.kind}-`)) && r.el && (r.el === el || el.contains(r.el))) out.push(u);
      }
    }
  }
  return out;
}

function takeBack(exprs, startLine, endLine) {
  const undo = exprs.flatMap(e => undoFor(e));
  if (!undo.length) { logOutput("nothing on this line to stop", "info"); return; }
  const ran = undo.map(e => runExpression(e)).every(Boolean);
  flashLines(startLine, endLine, ran ? "ok" : "err");
}

// =================================================================
//  FOLDER
//  A folder on this computer that the cues read from first
//  (mediaFolder.js): folder() says what is loaded, folder(open)
//  asks for one -- run by hand only: a browser opens no picker
//  for a scene or a room's line -- folder(list), folder(clear).
// =================================================================

function folderCue(arg, byHand) {
  const word = arg.replace(/^["']|["']$/g, "").trim().toLowerCase();
  if (!word) { logOutput(describeFolder(), "info"); return true; }
  if (word === "open") {
    if (!byHand) return null;
    pickFolder().then(said => { if (said) logOutput(said, "ok"); }).catch(err => logOutput(`folder: ${err.message}`, "err"));
    return true;
  }
  if (word === "list") {
    const paths = folderPaths();
    if (!paths.length) { logOutput(describeFolder(), "info"); return true; }
    for (const p of paths.slice(0, 40)) logOutput(p, "info");
    if (paths.length > 40) logOutput(`... and ${paths.length - 40} more`, "info");
    return true;
  }
  if (word === "clear") {
    logOutput(clearFolder() ? "folder cleared: the cues read from the server only" : "no folder to clear", "info");
    return true;
  }
  logOutput(`folder() takes open, list or clear -- not ${word}`, "err");
  return false;
}


// =================================================================
//  EXECUTION
//  Every line goes through the existing dispatch pipeline.
//  Animation cues need a target element; everything else is
//  elementless (synth, audio, speed, nav, osc, stop, ...).
// =================================================================

/** Cues that act on a score element and do nothing without one. */
const ELEMENT_CUE_TYPES = new Set([
  "cueTrans", "cueRotate", "cueScale", "cueO2P", "cueColor", "cuePin",
]);
const needsElement = ast =>
  ELEMENT_CUE_TYPES.has(ast.type) || (ast.type === "cueDraw" && !isDrawControl(ast));
/**
 * Cues a target is optional to: a > scatter(...) the target, scatter(...) the
 * stage; sh1 > image(...) in that shape's box, image(...) a frame of its own.
 */
const TARGET_OPTIONAL = new Set(["cueScatter", "cueImage", "cueStrobe"]);

/** Run `then` once `name` is drawn (findTargetElement), or say so after TARGET_WAIT_MS. */
const TARGET_WAIT_MS = 4000;
// A wait is keyed by what waits (the target and the line, ownerSignal): the
// same line pressed again while it waits replaces its wait, so it runs once
// when the box comes, not once a press. cancelDrawnWaits lets them all go.
const DRAWN_WAITS = "runWhenDrawn";
function runWhenDrawn(name, then, key = name) {
  const signal = ownerSignal(DRAWN_WAITS, key);
  const t0 = performance.now();
  const done = () => releaseOwner(DRAWN_WAITS, key);
  const id = setInterval(() => {
    if (findTargetElement(name)) { done(); then(); }
    else if (performance.now() - t0 > TARGET_WAIT_MS) { done(); logOutput("target not found: " + name, "err"); }
  }, 100);
  signal.addEventListener("abort", () => clearInterval(id), { once: true });
}

/** Every line still waiting for its target to be drawn, let go (a cleared stage: liveExamples.js). */
export function cancelDrawnWaits() {
  releaseOwner(DRAWN_WAITS);
}
onReset("stage", cancelDrawnWaits);

/** Score-view target lookup: registry uid, DOM id, then partial uid. */
function findTargetElement(idOrUid) {
  const reg = window.oscillaAnimRegistry;
  // a sound's name finds its box: s3 > rotate(...) turns synth s3's scope
  const el = reg[idOrUid]?.el || document.getElementById(idOrUid)
    || document.getElementById(`scope-${idOrUid}`);
  if (el) return el;
  const match = Object.keys(reg).find(k => k.includes(idOrUid));
  return match ? reg[match].el : null;
}

/**
 * What each line of the buffer last set: line -> "cueViz|hiss" -> the keys.
 * The same line run again with a setting taken out (scroll:right deleted)
 * puts that setting back first (liveEval.js resetsFor) -- the line is what
 * the box, shape or screen now is. Another line (viz(s, hue:120) below) is
 * a tweak, and leaves what it does not say alone. A line is followed
 * through the edits around it (moveLineMarks): one typed above does not
 * take its place.
 */
let lineSet = new Map();
let _lineSetText = "";
function followLineSet() {
  if (!editorEl || editorEl.value === _lineSetText) return;
  lineSet = moveLineMarks(lineSet, _lineSetText, editorEl.value);
  _lineSetText = editorEl.value;
}
function resetDropped(entry) {
  const now = lineSettings(entry.expr);
  if (!now.length) return;
  followLineSet();
  const was = lineSet.get(entry.startLine) ?? new Map();
  const before = new Map(now.map(s => [s.key, was.get(s.key)]).filter(([, v]) => v));
  for (const expr of resetsFor(before, now)) runExpression({ expr });
  for (const s of now) was.set(s.key, s.keys);
  lineSet.set(entry.startLine, was);
}

function executeLine(entry) {
  if (entry?.startLine != null) entry = nameSounds(entry);
  if (entry?.startLine != null) resetDropped(entry);
  const ran = runExpression(entry);
  if (ran != null && entry?.startLine != null) flashLines(entry.startLine, entry.endLine, ran ? "ok" : "err");
  return ran;
}

/**
 * Give every unnamed sound cue in the block a uid, written into the text
 * (synth(...) -> synth(uid:s1, ...)), so running the block again updates
 * that voice instead of starting another nobody can reach. Undoable; the
 * caret stays where it was. Returns the block as it now reads.
 */
function nameSounds(entry) {
  if (!editorEl || editorEl.readOnly || activeTab !== "local") return entry;
  const { expr } = splitInlineTarget(entry.expr.replace(/\s+/g, " ").trim());
  const checked = checkExpression(expr);
  if (checked.error) return entry;

  const lines = editorEl.value.split("\n");
  const blockStart = lines.slice(0, entry.startLine).reduce((n, l) => n + l.length + 1, 0);
  const blockText = lines.slice(entry.startLine, entry.endLine + 1).join("\n");
  const plan = planUids(blockText, checked.parts, fullText());
  if (!plan.length) return entry;

  const caret = editorEl.selectionStart;
  for (const p of [...plan].reverse()) {
    editorEl.setSelectionRange(blockStart + p.at, blockStart + p.at);
    insertText(p.insert);
  }
  const shift = plan.filter(p => blockStart + p.at <= caret).reduce((n, p) => n + p.insert.length, 0);
  editorEl.setSelectionRange(caret + shift, caret + shift);
  return expressionAtLine(editorEl.value, entry.startLine) || entry;
}

/**
 * map:'s handles shown (mapping on) or hidden (for the show): here, and on
 * the projector, which shows them too -- what is fitted is seen where it
 * lands.
 */
function mapHandlesCue(word) {
  const on = word === "show" || word === "on";
  setHandlesShown(on);
  // the latest kept for a projector joining late, not every press
  const i = mirrorLog.findIndex(m => m.key === "mapShown");
  if (i >= 0) mirrorLog.splice(i, 1);
  mirrorRun({ mapShown: on, key: "mapShown" });
  logOutput(on ? "map: handles shown -- drag them onto the surfaces; Ctrl+Shift+M hides them" : "map: handles hidden", "info");
  return true;
}

/**
 * What the stage set by hand -- map:'s fit, let go of a handle (shape.js
 * writeMap) -- written into the line that says it, so a run again or a
 * reload keeps it: the value replaced where the key is said, or the key
 * added to the line that made the thing (liveEval.js writeKeyPlan).
 * Undoable while the console is open; the caret stays where it was.
 */
window.addEventListener("oscilla:write-key", (e) => {
  if (FOLLOW || !editorEl || editorEl.readOnly || activeTab !== "local") return;
  const plan = writeKeyPlan(editorEl.value, e.detail || {});
  if (!plan) return;
  const caret = editorEl.selectionStart, end = editorEl.selectionEnd;
  const moved = n => (n <= plan.from ? n : n >= plan.to ? n + plan.insert.length - (plan.to - plan.from) : plan.from + plan.insert.length);
  if (isOpen) {
    editorEl.setSelectionRange(plan.from, plan.to);
    insertText(plan.insert);
  } else {
    editorEl.setRangeText(plan.insert, plan.from, plan.to);
    editorEl.dispatchEvent(new Event("input"));
  }
  editorEl.setSelectionRange(moved(caret), moved(end));
});

/**
 * Ctrl+Alt+Enter, while its line runs: a poly: synth gives the line to every
 * voice it has sounding (a part transposed at once) instead of adding one.
 */
let runAllVoices = false;

/**
 * Run one expression (a `{ expr }` from scanExpressions, or a string).
 * Nothing is dispatched unless every cue in it parses, and every outcome
 * lands in the output: ok, a parse error pointing at the word, a missing
 * target, or a handler that failed after it started. true: dispatched,
 * false: refused (and said why), null: nothing to run.
 */
function runExpression(entry) {
  const line = typeof entry === "string" ? entry : entry?.expr;
  const cleaned = (line || "").replace(/\s+/g, " ").trim();
  if (!cleaned || cleaned.startsWith("//") || cleaned.startsWith("#")) return null;

  const { target: inlineTarget, expr } = splitInlineTarget(cleaned);

  // folder() / folder(open|list|clear) -- this computer's files for the cues
  const folderMatch = expr.match(/^folder\(\s*(.*?)\s*\)$/i);
  if (folderMatch) return folderCue(folderMatch[1], entry?.startLine != null);
  // projector() -- open the projector window (follow mode)
  if (/^projector\(\s*\)$/i.test(expr)) {
    openProjector();
    return true;
  }
  // map(show) / map(hide) / map() -- map:'s handles, here and on the projector
  const mapWord = expr.match(/^map\(\s*(show|hide|on|off)?\s*\)$/i);
  if (mapWord) return mapHandlesCue(mapWord[1] ? mapWord[1].toLowerCase() : (handlesShown() ? "hide" : "show"));
  // room(jam) -- the room's mode, this machine's hearing (roomCue)
  const room = expr.match(/^room\(\s*(.*?)\s*\)$/i);
  if (room) return roomCue(room[1]);
  // snapshot(calm) -- the block it is in, kept (liveSnapshots.js): run by hand
  // only, at its line -- a scene or running everything passes it by
  const snap = expr.match(/^snapshot\(\s*([\w-]*)\s*\)$/i);
  if (snap) return entry?.startLine == null ? null : snapshotHere(entry.startLine, snap[1]);
  // versions() -- the code's file as it was: listed, or versions(3) put back
  const vers = expr.match(/^versions\(\s*(\d*)\s*\)$/i);
  if (vers) { versionsCue(vers[1] ? Number(vers[1]) : null); return true; }
  // clock() / clock(midi) / clock(own) -- what the beat follows (liveClock.js);
  // downbeat() -- a bar starts on the next beat, in the room too;
  // latency(80) -- this machine's speakers are heard 80 ms late
  const clk = expr.match(/^clock\(\s*(.*?)\s*\)$/i);
  if (clk) return clockCue(clk[1], logOutput);
  if (/^downbeat\(\s*\)$/i.test(expr)) return downbeatCue(logOutput);
  const lat = expr.match(/^latency\(\s*(.*?)\s*\)$/i);
  if (lat) return latencyCue(lat[1], logOutput);

  const first = checkExpression(expr);
  if (first.error) {
    logError(first.error);
    return false;
  }
  // rand() rolled once, here: this window and the projector run the same
  // numbers. (A settled line that somehow will not parse runs as written.)
  // (Ctrl+Alt+Enter: not rolled -- each voice of a poly: sound draws its
  // own, so a chord built up stays a chord)
  const rolled = runAllVoices ? expr : settleRandom(expr);
  let settled = rolled;
  if (/\btempo\s*\(/.test(settled)) settled = settleTempo(settled, nextWholeBeat());
  const resettled = settled === expr ? first : checkExpression(settled);
  const checked = resettled.error ? first : resettled;
  const runExpr = resettled.error ? expr : settled;
  // a tempo() settled here: the room is told, and keeps it for whoever joins
  // later (roomBeat.js). A line from another console came settled already.
  if (settled !== rolled && runExpr === settled) {
    for (const { bpm, beat } of tempoChangesIn(settled)) shareTimeline({ ms: msAtBeat(beat), beat, bpm });
  }
  // shape(...) rotate(...): the shape is the element -- the dispatcher hands
  // it on (runShapeLine), so the line goes over whole and needs no target.
  // use(orion) rotate(...) likewise.
  const shapeLed = LINE_LEADERS.has(checked.parts[0].ast.type);
  const anyNeedsElement = !shapeLed && checked.parts.some(p => needsElement(p.ast));

  // Dedicated view: send via WebSocket to the score window
  if (isDedicatedView()) {
    let targetId = inlineTarget
      || selectedTargetId || targetInputEl?.value.trim() || null;

    // An expression picked from the score's cue list is also its element id.
    if (anyNeedsElement && !targetId && scoreCueIds.includes(expr)) {
      targetId = expr;
    }
    if (anyNeedsElement && !targetId) {
      logOutput("no target -- use  targetId > expr  or pick an element", "err");
      return false;
    }
    if (window.socket?.readyState !== WebSocket.OPEN) {
      logOutput("not connected to the server -- nothing sent", "err");
      return false;
    }
    // the score windows run it (socket.js handleLivecodeExec), the projector
    // its mirror (exec: a laptop in a jam has it as livecode_exec already),
    // each with this run's seed
    const seed = newSeed();
    const project = getProjectName();
    if (shapeLed) {
      sendSocketMessage("livecode_exec", { project, cueExpr: runExpr, targetId: null, allVoices: runAllVoices, source: expr, seed });
      mirrorRun({ exec: true, shapeLed, expr: runExpr, source: expr, seed, allVoices: runAllVoices, target: null, parts: [] });
      logOutput(cleaned, "ok");
      return true;
    }
    checked.parts.forEach((part, i) => {
      sendSocketMessage("livecode_exec", {
        project,
        cueExpr: part.expr,
        targetId: needsElement(part.ast) ? targetId : null,
        allVoices: runAllVoices,
        source: first.parts[i]?.expr,
        seed,
      });
    });
    mirrorRun({
      exec: true, shapeLed, expr: runExpr, source: expr, seed, allVoices: runAllVoices, target: targetId,
      parts: checked.parts.map((p, i) => ({ expr: p.expr, source: first.parts[i]?.expr, el: needsElement(p.ast) })),
    });
    logOutput(cleaned, "ok");
    if (rolled !== expr) logRolled(rolled);
    return true;
  }

  // Score view: local execution via handleCueTrigger
  let targetEl = null;
  if (inlineTarget) {
    targetEl = findTargetElement(inlineTarget);
    if (anyNeedsElement && !targetEl) {
      // a name this buffer gives a sound or shape, not drawn yet -- a
      // synth's box comes when it plays, a moment after its line (on the
      // beat): run when it is there, as viz() waits for it
      if (knownUids().includes(inlineTarget)) { runWhenDrawn(inlineTarget, () => runExpression(entry), `${inlineTarget}\n${expr}`); return true; }
      logOutput("target not found: " + inlineTarget, "err");
      return false;
    }
  } else if (anyNeedsElement) {
    targetEl = resolveCurrentTarget();
  }
  if (anyNeedsElement && !targetEl) {
    logOutput("no target -- use  targetId > expr  or pick an element", "err");
    return false;
  }

  logOutput(cleaned, "ok");
  if (rolled !== expr) logRolled(rolled);
  _lastEvalAt = performance.now();
  _lastMirrored = "";
  // what the line rolls again later (each hit of a signal, each member,
  // each voice) comes from this run's seed, here and on the projector
  const seed = newSeed();
  // shape(n:12, ...): one line per member, each rolled on its own -- the
  // same members here and on the projector
  const members = shapeLed ? expandCluster(expr) : null;
  if (members) {
    for (const m of members) {
      const one = settleRandom(m);
      mirrorRun({ shapeLed: true, expr: one, target: null, parts: [], seed });
      try { handleCueTrigger(one, false, true, null, { live: true, seed }); }
      catch (err) { logOutput(`shape: ${err?.message || err}`, "err"); }
    }
    return true;
  }
  mirrorRun({
    shapeLed,
    expr: runExpr,
    source: expr,
    seed,
    allVoices: runAllVoices,
    target: inlineTarget || targetEl?.id || null,
    parts: checked.parts.map((p, i) => ({ expr: p.expr, source: first.parts[i]?.expr, el: needsElement(p.ast) || (!!inlineTarget && TARGET_OPTIONAL.has(p.ast.type)) })),
  });
  if (shapeLed) {
    try { handleCueTrigger(runExpr, false, true, null, { live: true, allVoices: runAllVoices, source: expr, seed }); }
    catch (err) { logOutput(`shape: ${err?.message || err}`, "err"); }
    return true;
  }
  checked.parts.forEach((part, i) => {
    // Each part on its own, so an element is handed only to the cues that
    // act on one (a synth given an element would draw its scope there).
    // force=true: a livecoded cue always runs, however often it is sent.
    try {
      const takes = needsElement(part.ast) || (!!inlineTarget && TARGET_OPTIONAL.has(part.ast.type));
      // source: the part as written, before rand() was rolled (bang(s))
      const result = handleCueTrigger(part.expr, false, true, takes ? targetEl : null, { live: true, allVoices: runAllVoices, source: first.parts[i]?.expr, seed });
      if (result && typeof result.catch === "function") {
        result.catch(err => logOutput(`${part.expr.split("(")[0]}: ${err?.message || err}`, "err"));
      }
    } catch (err) {
      logOutput(`${part.expr.split("(")[0]}: ${err?.message || err}`, "err");
    }
  });
  return true;
}

function executeAll() {
  if (!editorEl) return;
  // everything, folded blocks too: those flash their head -- but in a buffer
  // that calls scene(), not the scenes' lines: scene() starts those (liveScenePlan.js)
  const text = fullText(), lines = text.split("\n");
  const all = scanExpressions(text);
  const leave = linesLeftToScenes(lines, all.map(e => e.startLine));
  const live = all.filter(e => !leave.has(e.startLine) && !CONSOLE_ONLY.test(e.expr));
  // what commented-out lines started, taken back first: the stage is what the live lines make
  const sceneLines = linesLeftToScenes(lines, lines.map((_, i) => i));
  for (const expr of commentedTakeBacks(text, live.map(e => e.expr), i => sceneLines.has(i))) runExpression({ expr });
  runEntriesInTurn(live, (entry, now) => {
    const at = visibleLine(folds, entry.startLine);
    if (at.folded || !now) {
      const ran = runExpression({ expr: entry.expr });
      if (at.folded && ran != null) flashLines(at.line, at.line, ran ? "ok" : "err");
    } else {
      executeLine(expressionAtLine(editorEl.value, at.line) || entry);
    }
  }, "all");
}

/**
 * Entries run in turn, as a scene's are (liveScenes.js): a wait(N) line puts
 * the ones after it N beats later, one wait after another adding up; a
 * level() fading in runs first within its stretch between waits. run(entry,
 * now): now false for the ones run later -- the text may have moved on by
 * then, so they run by what they said. A run under the same key again drops
 * what the last one still had waiting.
 */
function runEntriesInTurn(entries, run, key) {
  cancelWaits(key);
  let beats = 0, stretch = [];
  const flush = () => {
    const these = fadesFirst(stretch);
    stretch = [];
    if (!these.length) return;
    if (beats <= 0) for (const e of these) run(e, true);
    else waitLater(beats, () => { for (const e of these) run(e, false); }, key);
  };
  for (const e of entries) {
    const w = waitBeats(e.expr);
    if (w == null) { stretch.push(e); continue; }
    flush();
    beats += Math.max(0, w);
  }
  flush();
}


// =================================================================
//  WARNINGS AFTER AN EVALUATION
//  Handlers report trouble (a missing file, an unknown wave) with
//  console.warn/error, often a moment later from an async load. For a
//  short while after each evaluation those land in the output too, so
//  a performer sees them without opening devtools.
// =================================================================

const MIRROR_MS = 2500;
let _lastEvalAt = -Infinity;
let _consoleOriginals = null;

function formatConsoleArgs(args) {
  return args.map(a => {
    if (a instanceof Error) return a.message;
    if (typeof a === "string") return a;
    try { return JSON.stringify(a)?.slice(0, 200) ?? String(a); } catch { return String(a); }
  }).join(" ");
}

let _lastMirrored = "";
function mirrorRecent(kind, args) {
  if (!isOpen || performance.now() - _lastEvalAt > MIRROR_MS) return;
  const text = formatConsoleArgs(args);
  if (text.startsWith("[CueDSL] ❌ Parse errors")) return;   // shown by the checker
  // the same complaint from every member of a cluster: once is enough
  if (text === _lastMirrored) return;
  _lastMirrored = text;
  logOutput(text, kind);
}

function onUnhandledRejection(e) {
  mirrorRecent("err", [e.reason]);
}

function installWarningMirror() {
  if (_consoleOriginals) return;
  _consoleOriginals = { warn: console.warn, error: console.error };
  console.warn = (...args) => { _consoleOriginals.warn.apply(console, args); mirrorRecent("warn", args); };
  console.error = (...args) => { _consoleOriginals.error.apply(console, args); mirrorRecent("err", args); };
  window.addEventListener("unhandledrejection", onUnhandledRejection);
}

// =================================================================
//  OUTPUT LOG
// =================================================================

// other modules (capture.js) say things in the console's output too
window.oscillaLiveLog = (text, type) => logOutput(text, type);
// ... and read what is written in it (solo.js: a sound's line not yet run)
window.oscillaLiveText = () => fullText();

/** A line into the output: the last 50 kept, the newest in sight. */
function appendOutput(line) {
  outputEl.appendChild(line);
  while (outputEl.children.length > 50) outputEl.removeChild(outputEl.firstChild);
  outputEl.scrollTop = outputEl.scrollHeight;
}

function logOutput(text, type = "info") {
  if (!outputEl) return;
  const line = document.createElement("div");
  line.className = "livecode-output-line " + type;
  line.textContent = text;
  appendOutput(line);
}

/**
 * The line as it ran, its rand() / choose() rolled into numbers -- to see
 * what was chosen, and, clicked, copied: run it to hear the same again.
 */
function logRolled(rolled) {
  if (!outputEl) return;
  const line = document.createElement("div");
  line.className = "livecode-output-line rolled";
  line.textContent = "= " + rolled;
  line.title = "rolled: click to copy";
  line.addEventListener("click", () => {
    navigator.clipboard?.writeText(rolled).then(() => {
      line.classList.add("copied");
      setTimeout(() => line.classList.remove("copied"), 900);
    }).catch(() => {});
  });
  appendOutput(line);
}

/** A parse error: the message, then the expression with a caret under the spot. */
function logError({ message, expr, col }) {
  logOutput(message, "err");
  if (!outputEl || !expr) return;
  const pre = document.createElement("div");
  pre.className = "livecode-output-line err livecode-output-caret";
  pre.textContent = expr + "\n" + " ".repeat(Math.max(0, Math.min(col, expr.length))) + "^";
  appendOutput(pre);
}

function clearOutput() {
  if (outputEl) outputEl.innerHTML = "";
}


// =================================================================
//  CUE BROWSER  (Ctrl+Shift+E)
//  Navigate cue entries in the output with keyboard.
//  Arrow/j/k = navigate, Enter = insert into editor, Escape = exit.
// =================================================================

let _browseActive = false;
let _browseHandler = null;

function enterCueBrowse() {
  if (!scoreCueIds.length) {
    logOutput("no cues loaded -- open ?view=live&project=... ", "err");
    return;
  }

  _browseActive = true;
  browseIdx = 0;
  highlightBrowseEntry(0);
  logOutput("[ctrl+shift+e] browsing cues -- arrows/j/k, enter to insert, esc to cancel", "info");

  // Capture keydown at document level so textarea doesn't eat keys
  _browseHandler = (e) => {
    if (!_browseActive) return;

    switch (e.key) {
      case "ArrowDown":
      case "j":
        e.preventDefault();
        e.stopPropagation();
        browseIdx = Math.min(browseIdx + 1, scoreCueIds.length - 1);
        highlightBrowseEntry(browseIdx);
        break;
      case "ArrowUp":
      case "k":
        e.preventDefault();
        e.stopPropagation();
        browseIdx = Math.max(browseIdx - 1, 0);
        highlightBrowseEntry(browseIdx);
        break;
      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        insertCueAtCursor(scoreCueIds[browseIdx]);
        exitCueBrowse();
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        exitCueBrowse();
        break;
    }
  };

  document.addEventListener("keydown", _browseHandler, true);
}

function exitCueBrowse() {
  _browseActive = false;
  browseIdx = -1;

  if (outputEl) {
    outputEl.querySelectorAll(".livecode-cue-entry.browsing")
      .forEach(el => el.classList.remove("browsing"));
  }

  if (_browseHandler) {
    document.removeEventListener("keydown", _browseHandler, true);
    _browseHandler = null;
  }

  editorEl?.focus();
}

function highlightBrowseEntry(idx) {
  if (!outputEl) return;
  const entries = outputEl.querySelectorAll(".livecode-cue-entry");
  entries.forEach(el => el.classList.remove("browsing"));

  if (entries[idx]) {
    entries[idx].classList.add("browsing");
    entries[idx].scrollIntoView({ block: "nearest" });
  }
}

function insertCueAtCursor(cueExpr) {
  if (!editorEl || !cueExpr) return;

  // Collapse any embedded newlines (Inkscape long IDs) into spaces
  const clean = cueExpr.replace(/\s+/g, " ").trim();

  editorEl.focus();
  insertText(clean);
}

/** After an edit: the folds carried along, the mirror drawn, the buffer saved and sent. */
function onEditorInput(inputType = "") {
  placeFolds(inputType);
  followLineSet();
  // kept first: whatever the drawing does, the text is saved
  if (activeTab === "local") {
    debouncedSaveBuffer();
    debouncedSendBuffer();
  }
  renderMirror();
  sendViewSoon();
  // the Ctrl+Space list follows what is typed; what can go here otherwise
  // shows as a reminder in the line (hintAtCaret), never as a list
  if (_suggestList) openSuggestList(_suggestList.auto);
}

// Chrome puts text of several lines in a line at a time, an input event
// each -- a block unfolded was some twenty, each drawing the whole mirror
// again: half a second before it showed. Held while it goes in, run once.
let _inserting = null;

/**
 * Type text into the editor at the cursor. execCommand keeps the edit on
 * the textarea's undo stack; assigning .value would wipe that stack, and
 * Ctrl+Z would stop working after the first Tab.
 */
function insertText(text) {
  editorEl.focus();
  _inserting = { n: 0 };
  let done;
  try { done = document.execCommand("insertText", false, text); }
  finally {
    const { n } = _inserting;
    _inserting = null;
    if (n) onEditorInput("insertText");
  }
  if (!done) {
    editorEl.setRangeText(text, editorEl.selectionStart, editorEl.selectionEnd, "end");
    editorEl.dispatchEvent(new Event("input"));
  }
}


// =================================================================
//  LINE COPY / CUT / PASTE
//  With nothing selected, Ctrl+C and Ctrl+X take the whole line the
//  cursor is on, and pasting a line taken that way puts it in as a whole
//  line above the cursor's, without splitting the line -- so Ctrl+C,
//  Ctrl+V duplicates a line, and Ctrl+X, move, Ctrl+V moves one.
// =================================================================

let _copiedLine = null;           // the last whole line Ctrl+C / Ctrl+X took

/** [start, end) of the line around the cursor, end past its newline. */
function cursorLineRange() {
  const text = editorEl.value;
  const pos = editorEl.selectionStart;
  const start = text.lastIndexOf("\n", pos - 1) + 1;
  const nl = text.indexOf("\n", pos);
  return [start, nl === -1 ? text.length : nl + 1];
}

function onLineCopy(e) {
  if (editorEl.selectionStart !== editorEl.selectionEnd) { _copiedLine = null; return; }
  const [start, end] = cursorLineRange();
  let line = editorEl.value.slice(start, end);
  if (!line.endsWith("\n")) line += "\n";
  e.preventDefault();
  e.clipboardData.setData("text/plain", line);
  _copiedLine = line;
  if (e.type === "cut" && !editorEl.readOnly) {
    editorEl.setSelectionRange(start, end);
    document.execCommand("delete");      // undoable, like any other edit
  }
}

/**
 * Ctrl+Shift+/: the lines under the cursor or selection commented out with
 * // (at the shallowest indent, so they stay in line) -- or, all of them
 * commented already, back in. Undoable. (Run, commented code takes back
 * what it started: runAtCursor.)
 */
function toggleComment() {
  if (editorEl.readOnly) return;
  const text = editorEl.value;
  const a = editorEl.selectionStart, b0 = editorEl.selectionEnd;
  const b = Math.max(a, b0 - (b0 > a && text[b0 - 1] === "\n" ? 1 : 0));
  const start = text.lastIndexOf("\n", a - 1) + 1;
  const nl = text.indexOf("\n", b);
  const end = nl === -1 ? text.length : nl;
  const lines = text.slice(start, end).split("\n");
  const filled = lines.filter(l => l.trim());
  if (!filled.length) return;
  const out = filled.every(l => /^\s*\/\//.test(l))
    ? lines.map(l => l.replace(/^(\s*)\/\/ ?/, "$1"))
    : (() => {
        const indent = Math.min(...filled.map(l => l.match(/^\s*/)[0].length));
        return lines.map(l => (l.trim() ? l.slice(0, indent) + "// " + l.slice(indent) : l));
      })();
  const changed = out.join("\n");
  const col = a - start;
  editorEl.setSelectionRange(start, end);
  insertText(changed);                     // undoable, like any other edit
  if (a === b0 && lines.length === 1) {
    // the cursor where it was in its line, moved with the text
    const at = start + Math.max(0, Math.min(changed.length, col + changed.length - (end - start)));
    editorEl.setSelectionRange(at, at);
  } else editorEl.setSelectionRange(start, start + changed.length);
}

/** Ctrl+Shift+K: the lines under the cursor or selection, gone (undoable). */
function deleteLines() {
  if (editorEl.readOnly) return;
  const text = editorEl.value;
  const a = editorEl.selectionStart;
  // a selection ending at the start of a line does not take that line
  const b = Math.max(a, editorEl.selectionEnd - (editorEl.selectionEnd > a && text[editorEl.selectionEnd - 1] === "\n" ? 1 : 0));
  let start = text.lastIndexOf("\n", a - 1) + 1;
  const nl = text.indexOf("\n", b);
  let end = nl === -1 ? text.length : nl + 1;
  // the last line: take the newline before it instead
  if (nl === -1 && start > 0) start--;
  if (start === end) return;
  editorEl.setSelectionRange(start, end);
  document.execCommand("delete");        // undoable, like any other edit
  // the cursor at the start of the line that took its place
  const at = editorEl.selectionStart;
  const next = nl === -1 ? editorEl.value.lastIndexOf("\n", at - 1) + 1 : at;
  editorEl.setSelectionRange(next, next);
}

function onLinePaste(e) {
  const text = e.clipboardData?.getData("text/plain");
  if (!text || text !== _copiedLine || editorEl.readOnly) return;
  if (editorEl.selectionStart !== editorEl.selectionEnd) return;
  e.preventDefault();
  const pos = editorEl.selectionStart;
  const [start] = cursorLineRange();
  editorEl.setSelectionRange(start, start);
  insertText(text);
  // the cursor stays on the text it was on, now one line further down
  editorEl.setSelectionRange(pos + text.length, pos + text.length);
}


// =================================================================
//  STASH
//  Lines kept off screen until they are typed: Ctrl+Shift+S copies the
//  line under the cursor, or every line the selection touches, to the
//  project's stash (server/liveStash.js), and the suggestions offer them
//  back. The computer running Oscilla keeps vocab.live; every other laptop
//  in a shared session keeps its own, vocab-<its name>.live -- the server
//  picks which, from where the request comes and the name sent with it.
//  A projector window has none.
//
//  The computer running Oscilla has a global stash too, vocab.live beside
//  the projects: the patterns common to every set. Ctrl+Alt+Shift+S adds to
//  it; the suggestions offer the set's own lines first, then the global ones.
// =================================================================

let setPhrases = [];       // this project's stash
let globalPhrases = [];    // the global one (the host only)
let stashPhrases = [];     // both, the set's first: what the suggestions offer

function mergeStash() {
  stashPhrases = [...setPhrases, ...globalPhrases.filter(p => !setPhrases.includes(p))];
}

const stashUrl = (project) => `/api/live-stash/${encodeURIComponent(project)}` +
  `?performer=${encodeURIComponent(window.localClientName || "")}`;

async function loadStash(project) {
  setPhrases = []; globalPhrases = []; mergeStash();
  if (FOLLOW) return;
  try {
    const r = await fetch(stashUrl(project));
    if (!r.ok || bufferProject !== project) return;
    const res = await r.json();
    setPhrases = res.phrases || [];
    globalPhrases = res.global?.phrases || [];
    mergeStash();
  } catch { /* no server: no stash */ }
}

/** Ctrl+Shift+S: to the set's stash; Ctrl+Alt+Shift+S (global): to the global one. */
async function stashAtCursor(global = false) {
  if (FOLLOW) return;
  const text = editorEl.value;
  const { selectionStart: a, selectionEnd: b } = editorEl;
  const start = text.lastIndexOf("\n", a - 1) + 1;
  // a selection ending at the start of a line does not take that line
  const last = b > a && text[b - 1] === "\n" ? b - 1 : b;
  const nl = text.indexOf("\n", last);
  const lines = text.slice(start, nl === -1 ? text.length : nl);
  if (!lines.trim()) { logOutput("stash: nothing on this line", "err"); return; }
  const project = getProjectName();
  try {
    const r = await fetch(stashUrl(project), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: lines, scope: global ? "global" : "project" }),
    });
    const res = await r.json();
    if (!r.ok) throw new Error(res.error || r.statusText);
    if (bufferProject === project) {
      if (global) globalPhrases = res.phrases || [];
      else setPhrases = res.phrases || [];
      mergeStash();
    }
    const where = global ? "the global stash" : String(res.file || "").split(/[\\/]/).pop();
    logOutput(res.added ? `stashed in ${where} (${(res.phrases || []).length} there)` : "stash: already there", "info");
  } catch (err) {
    logOutput(`stash failed: ${err.message}`, "err");
  }
}


// =================================================================
//  TARGET SELECTION
// =================================================================

function resolveTarget(idOrUid) {
  if (!idOrUid) {
    clearTarget();
    return;
  }

  // Dedicated view: no local DOM -- just store the ID string
  if (isDedicatedView()) {
    selectedTargetId = idOrUid;
    if (targetInfoEl) targetInfoEl.textContent = idOrUid;
    logOutput("target: " + idOrUid, "info");
    return;
  }

  // 1. Animation registry by exact uid
  const reg = window.oscillaAnimRegistry?.[idOrUid];
  if (reg?.el) {
    setTarget(reg.el, idOrUid);
    return;
  }

  // 2. DOM by id
  const el = document.getElementById(idOrUid);
  if (el) {
    setTarget(el, idOrUid);
    return;
  }

  // 3. Partial match in registry
  const keys = Object.keys(window.oscillaAnimRegistry);
  const match = keys.find(k => k.includes(idOrUid));
  if (match && window.oscillaAnimRegistry[match]?.el) {
    setTarget(window.oscillaAnimRegistry[match].el, match);
    if (targetInputEl) targetInputEl.value = match;
    return;
  }

  logOutput("target not found: " + idOrUid, "err");
}

function setTarget(el, label) {
  // Remove old highlight
  if (selectedElement) {
    selectedElement.classList.remove("livecode-selected");
  }

  selectedElement = el;
  el.classList.add("livecode-selected");

  // Build info string
  if (targetInfoEl) {
    const uid = el.dataset?.animUid || "";
    const cfg = el._oscillaCfg;
    const kind = cfg?.kind || "";
    const running = !!uid && window.runningAnimations.has(uid);

    let info = "<span>" + el.tagName + "</span>";
    if (el.id) info += ' id="' + esc(el.id) + '"';
    if (uid) info += " uid=<span>" + esc(uid) + "</span>";
    if (kind) info += " [" + kind + "]";
    if (running) info += " (running)";

    targetInfoEl.innerHTML = info;
  }

  logOutput("target: " + label, "info");
}

function clearTarget() {
  if (selectedElement) {
    selectedElement.classList.remove("livecode-selected");
  }
  selectedElement = null;
  selectedTargetId = null;
  if (targetInfoEl) targetInfoEl.innerHTML = "";
  if (targetInputEl) targetInputEl.value = "";
}

function resolveCurrentTarget() {
  // If input has a value but no element is selected yet, try resolving
  const inputVal = targetInputEl?.value.trim();
  if (inputVal && !selectedElement) {
    resolveTarget(inputVal);
  }
  return selectedElement;
}


// =================================================================
//  ELEMENT PICKER
// =================================================================

function togglePicking() {
  if (isPicking) {
    stopPicking();
  } else {
    startPicking();
  }
}

function startPicking() {
  isPicking = true;
  if (pickBtnEl) pickBtnEl.classList.add("picking");

  document.addEventListener("click", pickerClickHandler, true);
  document.addEventListener("mouseover", pickerHoverHandler, true);
  document.addEventListener("mouseout", pickerUnhoverHandler, true);

  logOutput("pick mode -- click an SVG element", "info");
}

function stopPicking() {
  if (!isPicking) return;
  isPicking = false;
  if (pickBtnEl) pickBtnEl.classList.remove("picking");

  document.removeEventListener("click", pickerClickHandler, true);
  document.removeEventListener("mouseover", pickerHoverHandler, true);
  document.removeEventListener("mouseout", pickerUnhoverHandler, true);

  // Clean up any lingering hover highlights
  document.querySelectorAll(".livecode-highlight").forEach(el => {
    el.classList.remove("livecode-highlight");
  });
}

function pickerClickHandler(e) {
  // Ignore clicks inside the panel
  if (panelEl?.contains(e.target)) return;

  e.preventDefault();
  e.stopPropagation();

  const el = findMeaningfulElement(e.target, e.clientX, e.clientY);
  if (!el) { stopPicking(); return; }

  const uid = el.dataset?.animUid || "";
  const label = (uid || el.id || el.tagName).replace(/\s+/g, " ").trim();
  if (targetInputEl) targetInputEl.value = label;
  setTarget(el, label);

  if (editorEl) {
    // Read DSL from data-oscilla (browser-edited) or from id (legacy Inkscape-authored)
    const dslExpr = (el.getAttribute?.("data-oscilla") || (el.id?.includes("(") ? el.id : ""))
                      .replace(/\s+/g, " ").trim();
    if (dslExpr) {
      insertTargetPrefix(label, dslExpr);
    } else {
      insertTargetPrefix(label);
    }
    editorEl.focus();
  }

  stopPicking();
}

/**
 * Insert or replace an inline target prefix on the current editor line.
 * If newExpr is provided, replaces the whole line with "target > expr".
 * Otherwise prepends "target > " to the existing line content.
 */
function insertTargetPrefix(targetLabel, newExpr) {
  if (!editorEl) return;

  const text = editorEl.value;
  const pos  = editorEl.selectionStart;

  // Find current line boundaries
  const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
  let lineEnd = text.indexOf("\n", pos);
  if (lineEnd === -1) lineEnd = text.length;

  const currentLine = text.slice(lineStart, lineEnd);

  let replacement;
  if (newExpr) {
    // Full replacement: target > cueExpr
    replacement = targetLabel + " > " + newExpr;
  } else {
    // Strip any existing inline target prefix, then prepend new one
    const stripped = currentLine.replace(/^[^(>\n]+>\s*/, "").trim();
    replacement = targetLabel + " > " + stripped;
  }

  editorEl.setSelectionRange(lineStart, lineEnd);
  insertText(replacement);   // leaves the cursor at the end of the line
}

function pickerHoverHandler(e) {
  if (panelEl?.contains(e.target)) return;
  const el = findMeaningfulElement(e.target, e.clientX, e.clientY);
  if (el) el.classList.add("livecode-highlight");
}

function pickerUnhoverHandler(e) {
  if (panelEl?.contains(e.target)) return;
  const el = findMeaningfulElement(e.target, e.clientX, e.clientY);
  if (el) el.classList.remove("livecode-highlight");
}

// Walk up from a single element looking for a cue signal (animUid or data-oscilla).
// Returns the matching ancestor, or null if none found before body.
function _walkForCueEl(el) {
  let current = el;
  while (current && current !== document.body) {
    if (current.dataset?.animUid) return current;
    const dataOscilla = current.getAttribute?.("data-oscilla");
    if (dataOscilla && dataOscilla.includes("(")) return current;
    current = current.parentElement;
  }
  return null;
}

function findMeaningfulElement(el, clientX, clientY) {
  // 1. Walk up from the direct click target looking for animUid / data-oscilla
  const direct = _walkForCueEl(el);
  if (direct) return direct;

  // 2. Hit label overlays (divs appended to body) — resolve via registry
  const hitLabels = window._oscillaHitLabels;
  if (Array.isArray(hitLabels)) {
    for (const rec of hitLabels) {
      if (rec.hit && (rec.hit === el || rec.hit.contains(el))) {
        const entry = window.oscillaAnimRegistry?.[rec.uid];
        if (entry?.el) return entry.el;
      }
    }
  }

  // 3. elementsFromPoint — catches SVG elements hidden under overlays
  if (clientX !== undefined && clientY !== undefined) {
    const stack = document.elementsFromPoint(clientX, clientY);
    for (const stackEl of stack) {
      const found = _walkForCueEl(stackEl);
      if (found) return found;
    }
  }

  return null;
}


// =================================================================
//  SIGNAL MONITOR
//  The bus's own snapshot, drawn at 5 fps to avoid DOM thrash.
// =================================================================

function startSignalMonitor() {
  if (!signalRefreshTimer) {
    signalRefreshTimer = setInterval(() => {
      // hidden (settings, perform mode): nothing to draw
      if (!panelEl?.querySelector('[data-section="monitor"]')?.offsetParent) return;
      if (monitorTab === "signals") renderSignals();
      else renderOscMonitor();
    }, 200);
  }
}

function stopSignalMonitor() {
  if (signalRefreshTimer) {
    clearInterval(signalRefreshTimer);
    signalRefreshTimer = null;
  }
}

function renderSignals() {
  if (!signalsEl) return;

  const filter = signalFilterEl?.value.trim().toLowerCase() || "";

  const snap = ParamBus.snapshot("");

  const paths = Object.keys(snap).sort();
  const filtered = filter
    ? paths.filter(p => p.toLowerCase().includes(filter))
    : paths;

  let html = "";
  for (const path of filtered) {
    const val = snap[path];
    const display = typeof val === "number"
      ? (Number.isInteger(val) ? String(val) : val.toFixed(4))
      : String(val);

    html +=
      '<div class="livecode-signal-row">' +
      '<span class="livecode-signal-path">' + esc(path) + '</span>' +
      '<span class="livecode-signal-val">' + esc(display) + '</span>' +
      '</div>';
  }

  signalsEl.innerHTML = html;
}


// =================================================================
//  MONITOR TAB SWITCHING (signals / osc)
// =================================================================

function switchMonitorTab(tab) {
  if (tab === monitorTab) return;
  monitorTab = tab;

  // Toggle panel visibility
  if (signalsEl) signalsEl.style.display = (tab === "signals") ? "" : "none";
  if (oscMonitorEl) oscMonitorEl.style.display = (tab === "osc") ? "" : "none";

  // Update tab button active state
  if (panelEl) {
    panelEl.querySelectorAll("[data-monitor-tab]").forEach(btn => {
      btn.classList.toggle("active", btn.dataset.monitorTab === tab);
    });
  }

  // Clear filter when switching
  if (signalFilterEl) signalFilterEl.value = "";

  // Show/hide clear button (only useful for OSC tab)
  const clearBtn = panelEl?.querySelector('[data-action="clear-monitor"]');
  if (clearBtn) clearBtn.style.display = (tab === "osc") ? "" : "none";

  // Immediate render of the newly visible tab
  if (tab === "signals") renderSignals();
  else renderOscMonitor();
}

function clearActiveMonitor() {
  if (monitorTab === "osc") {
    if (window.oscillaOSCMonitor) window.oscillaOSCMonitor.clear();
    if (oscMonitorEl) oscMonitorEl.innerHTML = "";
  }
}


// =================================================================
//  OSC MONITOR
//  Reads from window.oscillaOSCMonitor (populated by oscillaOSCClient.js)
//  Rendered at 5 fps on the shared interval. Rows grey out after 3s.
// =================================================================

const OSC_STALE_MS = 3000;

/** A short source tag for the message type; the others drop their osc_ */
function oscSourceTag(type) {
  if (!type) return "";
  const map = {
    osc_control:       "ctrl",
    osc_rotate:        "rot",
    osc_value:         "val",
    osc_audio_pool:    "pool",
    osc_audio_impulse: "impulse",
  };
  return map[type] || type.replace(/^osc_/, "");
}

function renderOscMonitor() {
  if (!oscMonitorEl) return;

  const mon = window.oscillaOSCMonitor;
  if (!mon || mon.size === 0) {
    oscMonitorEl.innerHTML = "";
    return;
  }

  const filter = oscMonitorFilterEl?.value.trim().toLowerCase() || "";
  const now = Date.now();

  const entries = [...mon.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  let html = "";
  for (const [addr, entry] of entries) {
    if (filter && !addr.toLowerCase().includes(filter)) continue;

    const stale = (now - entry.lastUpdated) > OSC_STALE_MS;
    const tag = oscSourceTag(entry.type);
    const argsStr = entry.args
      .map(v => typeof v === "number"
        ? (Number.isInteger(v) ? String(v) : v.toFixed(3))
        : String(v))
      .join("  ");

    html +=
      '<div class="livecode-oscmon-row' + (stale ? ' stale' : '') + '">' +
      (tag ? '<span class="livecode-oscmon-tag">' + esc(tag) + '</span>' : '') +
      '<span class="livecode-oscmon-addr">' + esc(addr) + '</span>' +
      '<span class="livecode-oscmon-args">' + esc(argsStr) + '</span>' +
      '</div>';
  }

  oscMonitorEl.innerHTML = html;
}


// =================================================================
//  SECTION RESIZE (drag bars between editor / output / signals)
// =================================================================

const SECTION_MAP = {
  "editor-chat":    { above: '[data-section="editor"]',  below: '[data-section="chat"]' },
  "chat-monitor":   { above: '[data-section="chat"]',    below: '[data-section="monitor"]' },
  "monitor-output": { above: '[data-section="monitor"]', below: '[data-section="output"]' },
};

function initSectionResize(panel) {
  panel.querySelectorAll(".livecode-resize-bar").forEach(bar => {
    let startY = 0, aboveH = 0, belowH = 0;
    let aboveEl = null, belowEl = null;
    let isResizing = false;

    function startResize(clientY) {
      const key = bar.dataset.resize;
      const mapping = SECTION_MAP[key];
      if (!mapping) return false;

      aboveEl = panel.querySelector(mapping.above);
      belowEl = panel.querySelector(mapping.below);
      if (!aboveEl || !belowEl) return false;

      startY = clientY;
      aboveH = aboveEl.getBoundingClientRect().height;
      belowH = belowEl.getBoundingClientRect().height;

      // Lock both to explicit heights and remove flex growth
      aboveEl.style.flex = "none";
      belowEl.style.flex = "none";
      aboveEl.style.height = aboveH + "px";
      belowEl.style.height = belowH + "px";

      bar.classList.add("active");
      isResizing = true;
      return true;
    }

    function moveResize(clientY) {
      if (!isResizing) return;
      const dy = clientY - startY;
      const newAbove = Math.max(40, aboveH + dy);
      const newBelow = Math.max(40, belowH - dy);
      aboveEl.style.height = newAbove + "px";
      belowEl.style.height = newBelow + "px";
    }

    function endResize() {
      if (!isResizing) return;
      bar.classList.remove("active");
      isResizing = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endResize);
      window.removeEventListener("touchcancel", endResize);
    }

    function onMouseMove(ev) { moveResize(ev.clientY); }
    function onMouseUp() { endResize(); }
    function onTouchMove(ev) {
      if (ev.touches.length === 1) {
        moveResize(ev.touches[0].clientY);
        ev.preventDefault();
      }
    }

    bar.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (startResize(e.clientY)) {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
      }
    });

    bar.addEventListener("touchstart", (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        if (startResize(e.touches[0].clientY)) {
          window.addEventListener("touchmove", onTouchMove, { passive: false });
          window.addEventListener("touchend", endResize);
          window.addEventListener("touchcancel", endResize);
        }
      }
    }, { passive: false });
  });
}


// =================================================================
//  PANEL RESIZE (drag any edge or corner to resize the whole panel)
// =================================================================

const MIN_W = 260;
const MIN_H = 200;

/**
 * Edge zones: each one defines which axes it resizes and
 * whether it grows from the left/top (requiring position shift).
 */
const EDGES = [
  { cls: "livecode-edge-n",  cursor: "ns-resize",   dy: -1, dx:  0, shiftY: true  },
  { cls: "livecode-edge-s",  cursor: "ns-resize",   dy:  1, dx:  0 },
  { cls: "livecode-edge-w",  cursor: "ew-resize",   dy:  0, dx: -1, shiftX: true  },
  { cls: "livecode-edge-e",  cursor: "ew-resize",   dy:  0, dx:  1 },
  { cls: "livecode-edge-nw", cursor: "nwse-resize", dy: -1, dx: -1, shiftX: true, shiftY: true },
  { cls: "livecode-edge-ne", cursor: "nesw-resize", dy: -1, dx:  1, shiftY: true  },
  { cls: "livecode-edge-sw", cursor: "nesw-resize", dy:  1, dx: -1, shiftX: true  },
  { cls: "livecode-edge-se", cursor: "nwse-resize", dy:  1, dx:  1 },
];

function initPanelResize(panel) {
  for (const edge of EDGES) {
    const el = document.createElement("div");
    el.className = "livecode-edge " + edge.cls;
    el.style.cursor = edge.cursor;
    panel.appendChild(el);

    let startX = 0, startY = 0, startW = 0, startH = 0, startL = 0, startT = 0;
    let isResizing = false;

    function startResize(clientX, clientY) {
      const rect = panel.getBoundingClientRect();
      startX = clientX;
      startY = clientY;
      startW = rect.width;
      startH = rect.height;
      startL = rect.left;
      startT = rect.top;

      // Ensure left/top positioning
      panel.style.left   = startL + "px";
      panel.style.top    = startT + "px";
      panel.style.right  = "auto";
      panel.style.bottom = "auto";

      document.body.style.cursor = edge.cursor;
      isResizing = true;
    }

    function moveResize(clientX, clientY) {
      if (!isResizing) return;
      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      if (edge.dx !== 0) {
        if (edge.shiftX) {
          const newW = Math.max(MIN_W, startW - deltaX);
          panel.style.width = newW + "px";
          panel.style.left  = (startL + startW - newW) + "px";
        } else {
          panel.style.width = Math.max(MIN_W, startW + deltaX) + "px";
        }
      }

      if (edge.dy !== 0) {
        if (edge.shiftY) {
          const newH = Math.max(MIN_H, startH - deltaY);
          panel.style.height = newH + "px";
          panel.style.top    = (startT + startH - newH) + "px";
        } else {
          panel.style.height = Math.max(MIN_H, startH + deltaY) + "px";
        }
      }
    }

    function endResize() {
      if (!isResizing) return;
      isResizing = false;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endResize);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endResize);
      window.removeEventListener("touchcancel", endResize);
    }

    function onMouseMove(ev) { moveResize(ev.clientX, ev.clientY); }
    function onTouchMove(ev) {
      if (ev.touches.length === 1) {
        moveResize(ev.touches[0].clientX, ev.touches[0].clientY);
        ev.preventDefault();
      }
    }

    el.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      startResize(e.clientX, e.clientY);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", endResize);
    });

    el.addEventListener("touchstart", (e) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        startResize(e.touches[0].clientX, e.touches[0].clientY);
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", endResize);
        window.addEventListener("touchcancel", endResize);
      }
    }, { passive: false });
  }
}


// =================================================================
//  UTILITY
// =================================================================


/**
 * Programmatically open the live console panel.
 * Used by the view router (?view=live, ?view=signals).
 */
export { openPanel as showLiveConsole, closePanel as hideLiveConsole };

/** Perform mode (Ctrl+Shift+F) on or off: the code over the whole screen. */
export { setPerformMode as setLivePerform };

/** The keys, over the code (Ctrl+Shift+H): Live's corner has a button for it. */
export { toggleCheatsheet as toggleLiveKeys, cheatOpen as isLiveKeysOpen };

/** Every line of the buffer run, as Ctrl+Shift+Enter (an example, opened and started). */
export { executeAll as runAllLines };

/** True while the live console panel is on screen (Escape stack). */
export function isLiveConsoleOpen() { return isOpen; }
